import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, Tag, CheckCircle2, CalendarDays, Settings, Plus, X, Loader2, Search,
  Edit2, Copy, Trash2, AlertTriangle, Clock, Lock, Unlock, FileSpreadsheet, ChevronLeft,
  ChevronRight, Truck, Info, Check, Ban, Users, Building2, TrendingUp, ShieldCheck,
} from "lucide-react";
import { API, formatApiError } from "../auth/api";
import { parseDk } from "../lib/tourHelpers";
import SuggestInput from "../components/SuggestInput";
import ImportDialog from "../components/ImportDialog";
import AddressField, { formatAddress } from "../components/AddressField";
import { requestExternal, OwnershipNote } from "./ExternalCustomers";
import WeeklyHours, { weekFromRange, weekSummary } from "../components/WeeklyHours";

/**
 * Økonomi (logistik admin) - erstatter "Priser & Rapport".
 * Se PRICING_SPEC.md for reglerne. Al beregning sker i backend/pricing.py;
 * siden viser resultatet og lader admin vedligeholde regler, helligdage,
 * indstillinger og godkende chaufførernes rettelser.
 */

const fmtKr = (n) => n == null ? "-" : Number(n).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " kr";
const fmtMin = (m) => {
  if (m == null) return "-";
  const h = Math.floor(m / 60);
  return `${h}t ${String(Math.round(m % 60)).padStart(2, "0")}m`;
};
const fmtDate = (s) => {
  if (!s) return "-";
  try { return new Date(s + "T00:00:00").toLocaleDateString("da-DK", { weekday: "short", day: "2-digit", month: "short" }); }
  catch (_) { return s; }
};
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const card = "bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const chipBase = "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors";
const chipOn = "bg-emerald-600 border-emerald-600 text-white";
const chipOff = "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700";
const input = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
// Uden w-full - til felter med fast bredde (w-full vinder ellers over w-24 osv.).
const inputFit = input.replace("w-full ", "");
const label = "block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700";

const STATUS_META = {
  ok: null,
  missing_rule: { text: "Mangler pris", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  missing_time: { text: "Mangler tid", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  missing_qty: { text: "Mangler antal", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
};
// Lokal dato (YYYY-MM-DD). toISOString() er UTC og giver "i går" mellem kl. 00 og 02.
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* 2026-10-17: Hjælpetekster (vises ved ⓘ). Brugerens egen forklaring på en
   regel gemmes i `note` og vises sammen med standardteksten. */
const HELP = {
  base: "Grundpris: hver tur får præcis én grundpris. Passer flere regler, vinder den mest specifikke (kunde > lokation > leveringsadresse > opgavetype > materiale).",
  surcharge: "Tillæg: lægges oveni grundprisen. Højst ét tillæg af hver type pr. tur - igen vinder det mest specifikke. Et kundespecifikt tillæg på 0 kr fritager altså den kunde.",
  model: {
    fixed: "Fast pris: samme beløb pr. tur, uanset hvor lang tid den tager.",
    hourly: "Timepris: tiden fra chaufføren trykker \"På vej\" til turen afsluttes × timeprisen. Startgebyr, minimum og afrunding kan tilføjes, og pauser kan trækkes fra. Kører chaufføren flere ture samtidig, deles tiden mellem dem.",
    per_container: "Pris pr. container: beløbet × antal containere på turen (fx ved løs afhentning, hvor chaufføren vejer flere containere, eller én container pr. indholdslinje).",
    per_unit: "Pris pr. enhed: beløbet × antal paller, kolli, m³, ton ... på turens indholdslinjer i den valgte enhed. Har chaufføren registreret antallet ved afslutning, bruges det - ellers det planlagte. Et minimumsbeløb sikrer at små ture stadig koster noget.",
  },
  category: {
    weekend: "Weekend: lægges på ture der køres lørdag eller søndag.",
    holiday: "Helligdag: lægges på ture på helligdage - ud fra dansk kalender eller kundens egen kalender (se fanen Helligdage).",
    haster: "Haster: lægges på ture med prioritet Haster.",
    trailer: "Hænger: lægges på ture der er kørt med hænger. Containerture: når turen kørte i en hængerkørsel (flere containere på én gang - både den på bilen og dem på hængeren). Varekørsel: når turen havde hængere (planlagt, eller oplyst af chaufføren ved afslutning). Vil du give containeren på hængeren en anden pris, så brug \"Hængerkørsel\" i prisaftalen.",
    evening: "Aften/nat: lægges på ture der starter i et bestemt tidsrum, fx 18:00-06:00. Tidsrummet sættes under \"Flere betingelser\".",
    waiting: "Ventetid: betaling for tid chaufføren venter på adressen, fx når containeren er blokeret eller kunden ikke er klar. Afregnes pr. påbegyndt kvarter ud fra de ventetidsminutter der er registreret på turen. Chaufføren beder om ventetid efter turen (godkendes under Godkendelser), eller du skriver den på turen i Rapport. Ture uden ventetid får intet tillæg. Typisk som kr pr. time.",
    stop: "Ekstra stop: et beløb pr. stop ud over dem der er med i grundprisen (standard 2 = én afhentning + én aflevering). Stop der ikke kunne gennemføres tæller med - chaufføren kørte derhen.",
    other: "Andet: alt det der ikke passer i de andre typer, fx miljøtillæg eller bro/færge. Brug betingelserne til at styre hvilke ture det gælder.",
  },
};
// 2026-10-20: Hængerkørsel - containerture kørt flere sammen (bil + hænger).
const RUN_LABEL = { solo: "Solo (fuld pris)", truck: "På bilen (hængerkørsel)", trailer: "På hængeren" };
// 2026-10-25: Kørt på hængeren/bilen, men hentet på en anden plads end resten
// af kørslen - prissat som solo (fuld pris), når firmaet har valgt det.
const runText = (r) => (r.run_alone && r.run_role === "solo"
  ? `${r.run_position === "trailer" ? "På hængeren" : "På bilen"} · fuld pris (hentet på anden plads)`
  : r.run_role && r.run_role !== "solo" ? (r.run_role === "trailer" ? "På hængeren" : "På bilen") : "");
// 2026-10-23: Hvad de andre ture i kørslen er - "Pap (4113, på hængeren)".
const partnerText = (ps) => (ps || []).map(x => {
  const bits = [x.container, x.run_role === "trailer" ? "på hængeren" : x.run_role === "truck" ? "på bilen" : ""].filter(Boolean);
  return `${x.material || x.tour_number || "tur"}${bits.length ? ` (${bits.join(", ")})` : ""}`;
}).join(" + ");
const ruleHelp = (r) => r.kind === "base"
  ? (HELP.model[(r.pricing || {}).model || "fixed"] || HELP.base)
  : (HELP.category[r.category] || HELP.surcharge);

/** ⓘ med forklaring. Klik/tryk åbner (virker også på telefon og iPad). */
function InfoTip({ text, own, className = "" }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btn = useRef(null);
  const pop = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!btn.current?.contains(e.target) && !pop.current?.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    window.addEventListener("scroll", onScroll, true);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("touchstart", close); window.removeEventListener("scroll", onScroll, true); };
  }, [open]);
  if (!text && !own) return null;
  const width = Math.min(320, window.innerWidth - 24);
  return (
    <>
      <button ref={btn} type="button" aria-label="Forklaring"
        onClick={(e) => { e.stopPropagation(); setRect(btn.current.getBoundingClientRect()); setOpen(v => !v); }}
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 ${own ? "text-emerald-600" : ""} ${className}`}>
        <Info className="w-4 h-4" />
      </button>
      {open && rect && createPortal(
        <div ref={pop} role="tooltip" className="fixed z-[1000] rounded-xl bg-slate-900 text-white text-xs leading-relaxed shadow-2xl p-3 space-y-2"
          style={{ width, top: Math.min(rect.bottom + 6, window.innerHeight - 200), left: Math.max(12, Math.min(rect.left - width / 2 + 12, window.innerWidth - width - 12)) }}>
          {text && <p>{text}</p>}
          {own && <div className="border-t border-white/15 pt-2"><div className="font-bold text-emerald-300 mb-0.5">Jeres forklaring</div><p className="whitespace-pre-wrap">{own}</p></div>}
        </div>,
        document.body
      )}
    </>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex flex-wrap gap-1 p-1 bg-slate-100 dark:bg-slate-900 rounded-lg">
      {options.map(o => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold ${value === o.value ? "bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Multi-valg med søgning. `items` = [{value, label}] ; tom værdi = "Alle". */
function MultiPick({ title, items, value, onChange, allLabel = "Alle" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = new Set(value || []);
  // 2026-10-27: `hint` (fx hvem leveringsadressen hører til) vises under navnet og kan søges på.
  const shown = items.filter(i => !q || `${i.label} ${i.hint || ""}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => {
    const s = new Set(selected);
    s.has(v) ? s.delete(v) : s.add(v);
    onChange(Array.from(s));
  };
  return (
    <div>
      <span className={label}>{title}</span>
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.size === 0 && <span className="px-2.5 py-1 rounded-full text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300">{allLabel}</span>}
        {items.filter(i => selected.has(i.value)).map(i => (
          <span key={i.value} title={i.hint || undefined} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
            {i.label}
            <button type="button" onClick={() => toggle(i.value)} aria-label={`Fjern ${i.label}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        <button type="button" onClick={() => setOpen(o => !o)} className="px-2.5 py-1 rounded-full text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30">
          {open ? "Luk" : "+ Vælg"}
        </button>
      </div>
      {open && (
        <div className="mt-2 border border-slate-200 dark:border-slate-600 rounded-lg p-2 bg-slate-50 dark:bg-slate-900">
          {items.length > 6 && (
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
              <input className={`${input} pl-8 py-1.5`} placeholder="Søg..." value={q} onChange={e => setQ(e.target.value)} />
            </div>
          )}
          <div className="max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
            {shown.map(i => (
              <label key={i.value} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white dark:hover:bg-slate-800 text-sm cursor-pointer">
                <input type="checkbox" checked={selected.has(i.value)} onChange={() => toggle(i.value)} className="accent-emerald-600" />
                <span className="min-w-0"><span className="block truncate">{i.label}</span>
                  {i.hint && <span className="block text-[11px] text-slate-500 truncate" title={i.hint}>{i.hint}</span>}</span>
              </label>
            ))}
            {shown.length === 0 && <span className="text-xs text-slate-400 px-2">Ingen valgmuligheder</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================================
   Hovedkomponent
   ====================================================================== */
const fmtTs = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" });
  return time === "00.00" || time === "00:00" ? date : `${date} kl. ${time}`;
};

export default function EconomyPanel({ canEdit = true, onGoCustomers }) {
  const [view, setView] = useState("report");
  const [rulesFilter, setRulesFilter] = useState("all");
  const [draftBundle, setDraftBundle] = useState(null);
  const [reportReload, setReportReload] = useState(0);
  const [options, setOptions] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const loadOptions = useCallback(async () => {
    try { setOptions((await axios.get(`${API}/pricing/options`)).data); }
    catch (e) { toast.error(formatApiError(e, "Kunne ikke hente valgmuligheder")); }
  }, []);
  const loadPending = useCallback(async () => {
    try { setPendingCount(((await axios.get(`${API}/pricing/requests`)).data || []).length); }
    catch (_) { /* ignore */ }
  }, []);
  useEffect(() => { loadOptions(); loadPending(); }, [loadOptions, loadPending]);

  const VIEWS = [
    { id: "report", label: "Rapport", icon: BarChart3 },
    { id: "customers", label: "Kunder & priser", icon: Users },
    { id: "rules", label: "Prisregler", icon: Tag },
    canEdit && { id: "approvals", label: "Godkendelser", icon: CheckCircle2, badge: pendingCount },
    { id: "holidays", label: "Helligdage", icon: CalendarDays },
    canEdit && { id: "settings", label: "Indstillinger", icon: Settings },
  ].filter(Boolean);

  return (
    <section className="space-y-4" data-testid="economy-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Økonomi</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Priser beregnes automatisk ud fra dine prisregler. Alle beløb er ekskl. moms.</p>
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
        {VIEWS.map(v => {
          const Icon = v.icon;
          return (
            <button key={v.id} onClick={() => setView(v.id)} data-testid={`economy-tab-${v.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${view === v.id ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"}`}>
              <Icon className="w-4 h-4" /> {v.label}
              {v.badge > 0 && <span className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[11px] font-bold">{v.badge}</span>}
            </button>
          );
        })}
      </div>
      {!options ? (
        <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {view === "report" && <ReportView options={options} onChanged={loadPending} goRules={() => setView("rules")} goApprovals={() => setView("approvals")} reloadSignal={reportReload}
            onCreatePrice={canEdit ? (row) => {
              // Turen uden pris: kunde + lokation + opgave + materiale(r) er udfyldt på forhånd.
              const mats = (row.materials && row.materials.length ? row.materials : (row.material ? [row.material] : []));
              setDraftBundle({ ...newBundle({
                customer_ids: row.customer_id ? [row.customer_id] : [],
                location_codes: row.location_code ? [row.location_code] : [],
                task_types: row.task_type ? [row.task_type] : [],
                materials: mats.length && mats.every(m => options.materials.includes(m)) ? mats : [],
              }, `${row.customer_name || "Kunde"}${row.location_name ? ` - ${row.location_name}` : ""}`),
              ...(row.outcome === "not_completed" ? { failed: { amount: "" } } : {}) });
            } : undefined} />}
          {draftBundle && <BundleEditor bundle={draftBundle} options={options} fromReport onClose={() => setDraftBundle(null)}
            onSaved={() => { setDraftBundle(null); setReportReload(n => n + 1); }} />}
          {!canEdit && (
            <div className="flex gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-sm text-amber-900 dark:text-amber-100">
              <Info className="w-4 h-4 mt-0.5 shrink-0" /> Du kan se priser, men ikke ændre dem. Spørg din administrator under Team &amp; rettigheder.
            </div>
          )}
          {view === "customers" && <CustomersView options={options} reloadOptions={loadOptions} canEdit={canEdit} onGoCustomers={onGoCustomers} goRules={(f) => { setRulesFilter(f); setView("rules"); }} />}
          {view === "rules" && <RulesView key={rulesFilter} initialFilter={rulesFilter} options={options} reloadOptions={loadOptions} canEdit={canEdit} />}
          {view === "approvals" && <ApprovalsView onChanged={loadPending} />}
          {view === "holidays" && <HolidaysView profile={options.profile} />}
          {view === "settings" && <SettingsView />}
        </>
      )}
    </section>
  );
}

/* ======================================================================
   Rapport
   ====================================================================== */
function periodRange(p) {
  const now = new Date();
  if (p === "this_month") return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  if (p === "last_month") return { from: ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: ymd(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (p === "this_week") {
    const d = new Date(now); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd);
    const e = new Date(d); e.setDate(d.getDate() + 6);
    return { from: ymd(d), to: ymd(e) };
  }
  return null;
}

function ReportView({ options, onChanged, goRules, goApprovals, onCreatePrice, reloadSignal = 0 }) {
  const [period, setPeriod] = useState("this_month");
  const [range, setRange] = useState(periodRange("this_month"));
  const [customer, setCustomer] = useState("");
  const [location, setLocation] = useState("");
  const [attention, setAttention] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [openTour, setOpenTour] = useState(null);

  useEffect(() => { const r = periodRange(period); if (r) setRange(r); }, [period]);

  const load = useCallback(async () => {
    if (!range.from || !range.to) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: range.from, date_to: range.to });
      if (customer) params.set("customer_id", customer);
      if (location) params.set("location_code", location);
      setData((await axios.get(`${API}/pricing/report?${params}`)).data);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e, "Kunne ikke hente rapporten")); }
    finally { setLoading(false); }
  }, [range.from, range.to, customer, location]);
  useEffect(() => { load(); }, [load, reloadSignal]);

  const rows = useMemo(() => {
    let arr = data?.rows || [];
    if (attention === "missing") arr = arr.filter(r => r.price.status !== "ok");
    if (attention === "pending") arr = arr.filter(r => r.pending_requests > 0);
    if (attention === "invoiced") arr = arr.filter(r => r.price.locked);
    if (attention === "open") arr = arr.filter(r => !r.price.locked);
    if (attention === "trailer") arr = arr.filter(r => !r.price.locked && r.trailer && r.trailer_confirmed === false);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(r => [r.tour_number, r.customer_name, r.location_name, r.material, r.driver_name, r.delivery_label, r.external_ref]
        .some(v => (v || "").toLowerCase().includes(q)));
    }
    return arr;
  }, [data, attention, search]);

  // 2026-10-23: Ture kørt sammen (hængerkørsel) står samlet under en
  // overskrift - prisen er stadig pr. tur. Kørslerne nummereres K1, K2 ...
  const display = useMemo(() => {
    const inRun = (r) => r.run_id && (r.run_position || (r.run_role && r.run_role !== "solo"));
    const byRun = {};
    rows.forEach(r => { if (inRun(r)) (byRun[r.run_id] = byRun[r.run_id] || []).push(r); });
    const out = [];
    const done = new Set();
    let n = 0;
    rows.forEach(r => {
      if (!inRun(r)) { out.push({ row: r }); return; }
      if (done.has(r.run_id)) return;
      done.add(r.run_id);
      n += 1;
      const members = [...byRun[r.run_id]].sort((a, b) => (a.run_seq || 0) - (b.run_seq || 0) || ((a.run_position || a.run_role) === "truck" ? -1 : 1));
      const outside = Math.max(0, (r.run_partners || []).length + 1 - members.length);
      const priced = members.filter(m => m.price.total != null);
      out.push({ header: { key: r.run_id, n, members, outside, total: priced.reduce((s, m) => s + m.price.total, 0),
        driver: r.driver_name, date: r.service_date,
        start: members.map(m => m.start_time).filter(Boolean).sort()[0] || "",
        end: members.map(m => m.end_time).filter(Boolean).sort().slice(-1)[0] || "" } });
      members.forEach((m, i) => out.push({ row: m, run: n, last: i === members.length - 1 }));
    });
    return out;
  }, [rows]);
  const runNo = useMemo(() => Object.fromEntries(display.filter(x => x.header).map(x => [x.header.key, x.header.n])), [display]);

  const locationsForCustomer = options.locations.filter(l => !customer || l.customer_id === customer);
  const t = data?.totals;
  const taskLabel = (k) => options.task_types[k] || k;

  const toggleAll = () => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map(r => r.tour_id)));
  // "Faktureret" fryser prisen. Markeringen kan kun fjernes fra ture der ER
  // faktureret - og kun ture med en pris kan markeres.
  const selectedRows = rows.filter(r => selected.has(r.tour_id));
  const toInvoice = selectedRows.filter(r => !r.price.locked && r.price.total != null);
  const toUninvoice = selectedRows.filter(r => r.price.locked);
  // 2026-10-25: Fakturanummeret fra regnskabsprogrammet gemmes på turene, og
  // åbne korrektioner (rettelser af fakturerede ture) kan komme med på samme faktura.
  const openCorr = (data?.corrections || []).filter(c => c.status === "open");
  // {no, date, corr: Set, custs: Set, only: bool} - only = en korrektion alene (kunden har ingen nye ture)
  const [invoiceAsk, setInvoiceAsk] = useState(null);
  const askCorr = invoiceAsk ? openCorr.filter(c => invoiceAsk.custs.has(c.customer_id)) : [];
  const markInvoiced = async (invoiced) => {
    if (invoiced) {
      const custs = new Set(toInvoice.map(r => r.customer_id));
      setInvoiceAsk({ no: "", date: new Date().toLocaleDateString("sv-SE"), custs, corr: new Set(openCorr.filter(c => custs.has(c.customer_id)).map(c => c.id)) });
      return;
    }
    const ids = toUninvoice.map(r => r.tour_id);
    if (!ids.length) return;
    const reason = window.prompt("Hvorfor fjernes markeringen? (fx markeret ved en fejl - fakturaen er ikke sendt).\nEr fakturaen sendt, så ret turen med en korrektion i stedet.");
    if (!reason || !reason.trim()) return;
    try {
      const { data: res } = await axios.post(`${API}/pricing/invoice`, { tour_ids: ids, invoiced: false, reason });
      toast.success(`Faktureret-markering fjernet fra ${res.updated} ture`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmInvoice = async () => {
    try {
      const { data: res } = await axios.post(`${API}/pricing/invoice`, {
        tour_ids: invoiceAsk.only ? [] : toInvoice.map(r => r.tour_id), invoiced: true, invoice_no: invoiceAsk.no.trim(), invoice_date: invoiceAsk.date,
        correction_ids: [...invoiceAsk.corr] });
      toast.success(invoiceAsk.only ? `Korrektionen er markeret${invoiceAsk.no.trim() ? ` (faktura/kreditnota ${invoiceAsk.no.trim()})` : ""}`
        : `${res.updated} ture${res.corrections ? ` + ${res.corrections} korrektioner` : ""} markeret som faktureret${invoiceAsk.no.trim() ? ` (faktura ${invoiceAsk.no.trim()})` : ""}${res.skipped_without_price ? ` · ${res.skipped_without_price} sprunget over (mangler pris)` : ""}`);
      setInvoiceAsk(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    const list = selected.size ? rows.filter(r => selected.has(r.tour_id)) : rows;
    if (!list.length) { toast.error("Ingen ture at eksportere"); return; }
    const num = (n) => n == null ? "" : String(n).replace(".", ",");
    const head = ["Kunde", "Dato", "Tur nr.", "Kundens ordrenr.", "Lokation", "Materiale", "Opgavetype", "Leveringsadresse", "Chauffør",
      "Start", "Slut", "Hænger", "Kørsel", "Ventetid (min)", "Prismodel", "Faktureret tid (min)", "Linjer", "Pris ekskl. moms", "Status"];
    const sorted = [...list].sort((a, b) => (a.customer_name || "").localeCompare(b.customer_name || "") || a.service_date.localeCompare(b.service_date));
    const lines = sorted.map(r => [
      r.customer_name, r.service_date, r.tour_number, r.external_ref || "", r.location_name, r.material, taskLabel(r.task_type), r.delivery_label,
      r.driver_name, r.start_time, r.end_time, r.trailer ? "Ja" : "Nej",
      r.run_role ? `${r.run_alone ? runText(r) : (RUN_LABEL[r.run_role] || r.run_role)}${runNo[r.run_id] ? ` · kørsel K${runNo[r.run_id]}` : ""}${(r.run_partners || []).length ? ` · sammen med ${partnerText(r.run_partners)}` : ""}` : "",
      r.waiting_min || 0,
      options.models[r.price.model] || "", r.price.time?.billable_min ?? "",
      (r.price.lines || []).map(l => `${l.label}: ${num(l.amount)}`).join(" | "),
      num(r.price.total), r.outcome === "not_completed" ? "Forgæves" : r.price.locked ? "Faktureret" : (STATUS_META[r.price.status]?.text || "OK"),
    ]);
    // 2026-10-25: Åbne korrektioner (rettelser af fakturerede ture) på egne linjer.
    const expCusts = new Set(list.map(r => r.customer_id));
    const corrLines = openCorr.filter(c => !selected.size || expCusts.has(c.customer_id)).map(c => [c.customer_name, c.service_date, c.tour_number, c.external_ref || "", "", "", "", "", "", "", "", "", "", "", "Korrektion", "",
      `Korrektion${c.original_invoice_no ? ` til faktura ${c.original_invoice_no}` : ""}: ${c.note}`, num(c.delta), "Korrektion"]);
    const csv = [head, ...lines, ...corrLines].map(row => row.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `fakturagrundlag_${range.from}_${range.to}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      <div className={`${card} p-4 space-y-3`}>
        <div className="flex flex-wrap gap-2">
          {[["this_month", "Denne måned"], ["last_month", "Sidste måned"], ["this_week", "Denne uge"], ["custom", "Brugerdef."]].map(([v, l]) => (
            <button key={v} onClick={() => setPeriod(v)} className={`${chipBase} ${period === v ? chipOn : chipOff}`}>{l}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div><span className={label}>Fra</span><input type="date" className={input} value={range.from} onChange={e => { setPeriod("custom"); setRange(r => ({ ...r, from: e.target.value })); }} /></div>
          <div><span className={label}>Til</span><input type="date" className={input} value={range.to} onChange={e => { setPeriod("custom"); setRange(r => ({ ...r, to: e.target.value })); }} /></div>
          <div><span className={label}>Kunde</span>
            <select className={input} value={customer} onChange={e => { setCustomer(e.target.value); setLocation(""); }}>
              <option value="">Alle kunder</option>
              {options.customers.some(c => c.own) && (
                <optgroup label="Egne kunder">
                  {options.customers.filter(c => c.own).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </optgroup>
              )}
              {options.customers.some(c => !c.own) && (
                <optgroup label="Genbrugsplads-kunder">
                  {options.customers.filter(c => !c.own).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </optgroup>
              )}
            </select></div>
          <div><span className={label}>Lokation</span>
            <select className={input} value={location} onChange={e => setLocation(e.target.value)}>
              <option value="">Alle lokationer</option>
              {locationsForCustomer.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select></div>
          <div className="col-span-2 md:col-span-1"><span className={label}>Søg</span>
            <input className={input} placeholder="Tur, ordrenr., materiale, chauffør..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        </div>
      </div>

      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi tone="emerald" title="Omsætning ekskl. moms" value={fmtKr(t.net)} sub={`Inkl. ${t.vat_percent}% moms: ${fmtKr(t.gross)}`} />
          <Kpi tone="blue" title="Ture" value={t.tours} sub={`${t.not_completed} forgæves · ${t.invoiced} faktureret`} />
          <Kpi tone="amber" title="Fakt. timer" value={`${String(t.billable_hours).replace(".", ",")} t`} sub="Timeprisede ture" />
          <Kpi tone="sky" title="Gns. pr. tur" value={fmtKr(t.tours - t.missing_rule - t.missing_time - (t.missing_qty || 0) > 0 ? t.net / (t.tours - t.missing_rule - t.missing_time - (t.missing_qty || 0)) : 0)} sub="Ture med pris" />
        </div>
      )}

      {t && (t.missing_rule > 0 || t.missing_time > 0 || t.missing_qty > 0 || t.pending_requests > 0 || t.trailer_unconfirmed > 0) && (
        <div className="flex flex-wrap gap-2">
          {t.missing_rule > 0 && (
            <button onClick={() => setAttention(attention === "missing" ? "" : "missing")} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="w-4 h-4" /> {t.missing_rule} ture mangler en prisregel
              <span className="underline" onClick={(e) => { e.stopPropagation(); goRules(); }}>Opret regel</span>
            </button>
          )}
          {t.missing_time > 0 && (
            <button onClick={() => setAttention(attention === "missing" ? "" : "missing")} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-sm text-amber-800 dark:text-amber-200">
              <Clock className="w-4 h-4" /> {t.missing_time} timeprisede ture mangler start/slut
            </button>
          )}
          {t.missing_qty > 0 && (
            <button onClick={() => setAttention(attention === "missing" ? "" : "missing")} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-sm text-amber-800 dark:text-amber-200">
              <AlertTriangle className="w-4 h-4" /> {t.missing_qty} ture med pris pr. enhed mangler antal
            </button>
          )}
          {t.trailer_unconfirmed > 0 && (
            <button onClick={() => setAttention(attention === "trailer" ? "" : "trailer")} data-testid="report-trailer-unconfirmed"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${attention === "trailer" ? "bg-amber-600 text-white border-amber-600" : "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-200"}`}>
              <Info className="w-4 h-4" /> {t.trailer_unconfirmed} ture med hænger, som chaufføren ikke har bekræftet - tjek før fakturering
            </button>
          )}
          {t.pending_requests > 0 && (
            <button onClick={goApprovals} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-sm text-amber-800 dark:text-amber-200">
              <CheckCircle2 className="w-4 h-4" /> {t.pending_requests} rettelser venter på godkendelse
            </button>
          )}
        </div>
      )}

      {data?.by_customer?.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {data.by_customer.map(b => (
            <button key={b.customer_id} onClick={() => setCustomer(customer === b.customer_id ? "" : b.customer_id)}
              className={`${card} px-4 py-2.5 text-left min-w-[180px] ${customer === b.customer_id ? "ring-2 ring-emerald-500" : ""}`}>
              <div className="text-xs font-semibold text-slate-500 truncate">{b.customer_name}</div>
              <div className="font-bold">{fmtKr(b.net)}</div>
              <div className="text-[11px] text-slate-500">{b.tours} ture{b.missing ? ` · ${b.missing} uden pris` : ""}</div>
            </button>
          ))}
        </div>
      )}

      {(data?.retro || []).length > 0 && <RetroCard items={data.retro} onDone={load} />}
      {(data?.corrections || []).length > 0 && (
        <div className={`${card} p-4 space-y-2`} data-testid="report-corrections">
          <div className="font-bold text-sm">Korrektioner til fakturerede ture</div>
          <p className="text-xs text-slate-500">En fejl fundet efter faktureringen rettes som en korrektion: den fakturerede tur står uændret, og forskellen kommer på kundens næste faktura (eller krediteres i regnskabsprogrammet). Når I markerer kundens næste ture som faktureret, er kundens åbne korrektioner valgt i forvejen - og de står i CSV-filen. Har kunden ingen nye ture, så lav en kreditnota/faktura i regnskabsprogrammet og tryk "Faktureret/krediteret".</p>
          <ul className="divide-y divide-slate-100 dark:divide-slate-700 text-sm">
            {data.corrections.map(c => (
              <li key={c.id} className="py-1.5 flex items-center gap-2">
                <span className="flex-1 min-w-0"><b>{c.customer_name}</b> · {c.tour_number || "tur"} {fmtDate(c.service_date)}{c.original_invoice_no ? ` · faktura ${c.original_invoice_no}` : ""} · {c.note}</span>
                <span className={`font-bold ${c.delta < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtKr(c.delta)}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.status === "open" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-700"}`}>{c.status === "open" ? "Åben" : `Faktura ${c.invoice_no || ""}`}</span>
                {c.status === "open" && (
                  <button className="text-[11px] font-semibold text-emerald-700 hover:underline" data-testid={`corr-invoice-${c.id}`}
                    onClick={() => setInvoiceAsk({ no: "", date: new Date().toLocaleDateString("sv-SE"), custs: new Set([c.customer_id]), corr: new Set([c.id]), only: true })}>
                    Faktureret/krediteret…</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {invoiceAsk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setInvoiceAsk(null)}>
          <div className={`${card} w-full max-w-md p-5 space-y-3`} onClick={e => e.stopPropagation()} data-testid="invoice-dialog">
            <div className="font-bold">{invoiceAsk.only ? "Korrektionen er faktureret eller krediteret" : "Markér som faktureret"}</div>
            <p className="text-sm text-slate-500">{invoiceAsk.only
              ? "Skriv nummeret på fakturaen eller kreditnotaen fra regnskabsprogrammet. Korrektionen står så som afsluttet."
              : "Prisen fryses. Skriv fakturanummeret fra jeres regnskabsprogram, så turene kan findes, hvis kunden spørger - ContainerFlow laver ikke selv fakturanumre (det gør regnskabsprogrammet)."}</p>
            <div className="grid grid-cols-2 gap-2">
              <div><span className={label}>Fakturanr.</span><input className={input} value={invoiceAsk.no} onChange={e => setInvoiceAsk({ ...invoiceAsk, no: e.target.value })} placeholder="fx 10234" autoFocus data-testid="invoice-no" /></div>
              <div><span className={label}>Fakturadato</span><input type="date" className={input} value={invoiceAsk.date} onChange={e => setInvoiceAsk({ ...invoiceAsk, date: e.target.value })} /></div>
            </div>
            {askCorr.length > 0 && (
              <div className="space-y-1">
                <div className={label}>Korrektioner med på fakturaen</div>
                {askCorr.map(c => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="accent-emerald-600" checked={invoiceAsk.corr.has(c.id)}
                      onChange={e => { const n = new Set(invoiceAsk.corr); e.target.checked ? n.add(c.id) : n.delete(c.id); setInvoiceAsk({ ...invoiceAsk, corr: n }); }} />
                    <span className="flex-1">{c.customer_name} · {c.note}</span><b>{fmtKr(c.delta)}</b>
                  </label>
                ))}
              </div>
            )}
            <div className="text-sm">I alt: <b>{fmtKr((invoiceAsk.only ? 0 : toInvoice.reduce((x, r) => x + (r.price.total || 0), 0)) + askCorr.filter(c => invoiceAsk.corr.has(c.id)).reduce((x, c) => x + c.delta, 0))}</b> ekskl. moms ({invoiceAsk.only ? "" : `${toInvoice.length} ture`}{invoiceAsk.corr.size ? `${invoiceAsk.only ? "" : " + "}${invoiceAsk.corr.size} korrektion${invoiceAsk.corr.size === 1 ? "" : "er"}` : ""})</div>
            <div className="flex justify-end gap-2">
              <button className={btnGhost} onClick={() => setInvoiceAsk(null)}>Annuller</button>
              <button className={btnPrimary} onClick={confirmInvoice} disabled={invoiceAsk.only && !invoiceAsk.corr.size} data-testid="invoice-confirm"><Lock className="w-4 h-4" /> {invoiceAsk.only ? "Markér som afsluttet" : "Markér som faktureret"}</button>
            </div>
          </div>
        </div>
      )}

      <div className={`${card} overflow-hidden`}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-700">
          <div className="flex flex-wrap gap-1.5">
            {[["", "Alle"], ["open", "Ikke faktureret"], ["invoiced", "Faktureret"], ["missing", "Mangler pris/tid"], ["pending", "Afventer godkendelse"]].map(([v, l]) => (
              <button key={v} onClick={() => setAttention(v)} className={`${chipBase} ${attention === v ? chipOn : chipOff}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {toInvoice.length > 0 && (
              <button className={btnGhost} onClick={() => markInvoiced(true)} title="Prisen fryses, så senere regelændringer ikke ændrer den">
                <Lock className="w-4 h-4" /> Markér som faktureret ({toInvoice.length})
              </button>
            )}
            {toUninvoice.length > 0 && (
              <button className={btnGhost} onClick={() => markInvoiced(false)} title="Prisen beregnes igen ud fra de nuværende regler">
                <Unlock className="w-4 h-4" /> Fjern faktureret-markering ({toUninvoice.length})
              </button>
            )}
            <button className={btnPrimary} onClick={exportCsv}><FileSpreadsheet className="w-4 h-4" /> {selected.size ? `Eksportér valgte (${selected.size})` : "Eksportér fakturagrundlag"}</button>
          </div>
        </div>
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">Ingen afsluttede ture i perioden.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/40 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="p-3 w-8"><input type="checkbox" className="accent-emerald-600" checked={selected.size === rows.length} onChange={toggleAll} /></th>
                  <th className="p-3 text-left">Dato</th>
                  <th className="p-3 text-left">Kunde · Lokation</th>
                  <th className="p-3 text-left">Opgave</th>
                  <th className="p-3 text-left">Chauffør</th>
                  <th className="p-3 text-left">Tid</th>
                  <th className="p-3 text-left">Model</th>
                  <th className="p-3 text-right">Pris</th>
                </tr>
              </thead>
              <tbody>
                {display.map(item => {
                  if (item.header) {
                    const h = item.header;
                    return (
                      <tr key={`run-${h.key}`} className="border-t border-violet-200 dark:border-violet-900 bg-violet-50 dark:bg-violet-950/30" data-testid={`report-run-${h.n}`}>
                        <td className="pl-3 py-2 border-l-4 border-violet-500" />
                        <td colSpan={7} className="px-3 py-2 text-xs text-violet-900 dark:text-violet-200">
                          <b>Hængerkørsel K{h.n}</b> · {h.members.length + h.outside} containere kørt sammen · {h.driver} · {fmtDate(h.date)}{h.start ? ` · ${h.start}–${h.end || "?"}` : ""}
                          {h.outside > 0 && <span className="text-violet-700"> · {h.outside} af turene er ikke med i udvalget</span>}
                          <span className="float-right font-bold">I alt {fmtKr(h.total)}</span>
                        </td>
                      </tr>
                    );
                  }
                  const r = item.row;
                  const st = STATUS_META[r.price.status];
                  return (
                    <tr key={r.tour_id} onClick={() => setOpenTour(r.tour_id)} className={`${item.run ? "bg-violet-50/40 dark:bg-violet-950/10" : "border-t border-slate-100 dark:border-slate-700"} hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer`}>
                      <td className={`p-3 ${item.run ? "border-l-4 border-violet-500" : ""}`} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" className="accent-emerald-600" checked={selected.has(r.tour_id)}
                          onChange={() => setSelected(s => { const n = new Set(s); n.has(r.tour_id) ? n.delete(r.tour_id) : n.add(r.tour_id); return n; })} />
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        <div className="font-semibold">{fmtDate(r.service_date)}</div>
                        <div className="text-[11px] text-slate-500">{r.tour_number || ""}{r.external_ref ? <span title="Kundens ordrenr."> · {r.external_ref}</span> : null} {r.day_type !== "weekday" && <span className="text-amber-600">· {options.day_types[r.day_type]}</span>}</div>
                      </td>
                      <td className="p-3"><div className="font-semibold">{r.customer_name}</div><div className="text-xs text-slate-500">{r.location_name}</div></td>
                      <td className="p-3">
                        <div>{(r.materials && r.materials.length > 1) ? r.materials.join(" + ") : r.material}</div>
                        <div className="text-xs text-slate-500">{taskLabel(r.task_type)}{r.priority === "haster" ? " · Haster" : ""}{runText(r) ? ` · ${runText(r)}${(r.run_partners || []).length ? ` · sammen med ${partnerText(r.run_partners)}` : ""}` : r.trailer ? ` · ${r.trailer_count > 1 ? `${r.trailer_count} hængere` : "Hænger"}` : ""}
                          {r.trailer && r.trailer_confirmed === false && !r.price.locked && <span className="ml-1 px-1 rounded bg-amber-100 text-amber-800 text-[10px] font-bold" title="Foreslået fra chaufførens profil ved start - chaufføren har ikke svaret">automatisk, ikke bekræftet</span>}</div>
                      </td>
                      <td className="p-3 whitespace-nowrap">{r.driver_name}</td>
                      <td className="p-3 whitespace-nowrap font-mono text-xs">{r.start_time || "-"}–{r.end_time || "-"}</td>
                      <td className="p-3 text-xs">
                        {r.outcome === "not_completed" ? <span className="text-red-600 font-semibold">Forgæves</span> : (options.models[r.price.model] || "-")}
                        {r.price.time && <div className="text-slate-500">{fmtMin(r.price.time.billable_min)}</div>}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <div className="font-bold">{fmtKr(r.price.total)}</div>
                        {/* 2026-10-25: Faktureret tur med korrektion - den nye pris (før så det ud, som om rettelsen ikke virkede). */}
                        {r.correction && <div className={`text-[11px] font-semibold ${r.correction.open ? "text-amber-700" : "text-slate-500"}`} data-testid={`row-correction-${r.tour_id}`}
                          title={`Korrektion ${fmtKr(r.correction.delta)}${r.correction.open ? " - kommer på næste faktura" : " - faktureret"}`}>→ {fmtKr(r.correction.total)} {r.correction.open ? "(korrektion åben)" : "(korrigeret)"}</div>}
                        <div className="flex justify-end gap-1 mt-0.5">
                          {st && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${st.cls}`}>{st.text}</span>}
                          {r.price.status === "missing_rule" && onCreatePrice && (
                            <button className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white" data-testid={`create-price-${r.tour_id}`}
                              onClick={(e) => { e.stopPropagation(); onCreatePrice(r); }}>Opret pris</button>
                          )}
                          {r.price.locked && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">Faktureret</span>}
                          {r.has_override && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Tilpasset</span>}
                          {r.pending_requests > 0 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">Afventer</span>}
                        </div>
                        {r.price.why?.text && <div className="text-[10px] text-red-700 dark:text-red-300 mt-0.5 max-w-[240px] ml-auto whitespace-normal" data-testid={`why-${r.tour_id}`}>{r.price.why.text}</div>}
                        {/* 2026-10-25: Det chaufføren meldte - set før fakturering. */}
                        {(r.not_completed_code || r.finish_issue || (r.waiting_reported_min > 0 && !r.waiting_min)) && (
                          <div className="text-[10px] text-amber-800 dark:text-amber-200 mt-0.5 max-w-[240px] ml-auto whitespace-normal" data-testid={`driver-note-${r.tour_id}`}>
                            {[r.not_completed_code && `Forgæves: ${r.not_completed_code}${r.not_completed_comment ? ` - ${r.not_completed_comment}` : ""}`,
                              r.finish_issue && `Afvigelse: ${r.finish_issue}`,
                              r.waiting_reported_min > 0 && !r.waiting_min && `Ventetid oplyst: ${r.waiting_reported_min} min (ikke godkendt)`].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {openTour && <TourPriceDrawer tourId={openTour} options={options} onClose={() => setOpenTour(null)} onChanged={() => { load(); onChanged(); }} />}
    </div>
  );
}

function Kpi({ tone, title, value, sub }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900",
    blue: "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900",
    amber: "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900",
    sky: "bg-sky-50 border-sky-200 dark:bg-sky-950/30 dark:border-sky-900",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ======================================================================
   Tur-detaljer: prisopbygning + tilpasning
   ====================================================================== */
function TourPriceDrawer({ tourId, options, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [corrMode, setCorrMode] = useState(false);   // 2026-10-25: rettelse af en faktureret tur

  const load = useCallback(async () => {
    try { setD((await axios.get(`${API}/pricing/tours/${tourId}`)).data); }
    catch (e) { toast.error(formatApiError(e)); onClose(); }
  }, [tourId, onClose]);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    const fo = d.fact_overrides || {}, po = d.price_override || {};
    setEdit({
      start_time: fo.start_time || "", end_time: fo.end_time || "",
      trailer_count: fo.trailer_count !== undefined ? String(fo.trailer_count) : fo.used_trailer === undefined ? "" : (fo.used_trailer ? String(d.trailer_count || 1) : "0"),
      run_role: fo.run_role || "",
      waiting_min: fo.waiting_min ?? "", deduct_pause: fo.deduct_pause === undefined ? "" : String(fo.deduct_pause),
      mode: po.mode === "fixed" ? "fixed" : "rules", amount: po.amount ?? "",
      add_waiting: !!po.add_waiting, label: po.label || "",
      extra_lines: po.extra_lines || [], note: "",
    });
  };
  const save = async () => {
    if (!edit.note.trim()) { toast.error("Skriv en kort begrundelse"); return; }
    // 2026-10-25: "1.500,50" blev før til 0 kr (talfelt), og en linje uden tekst forsvandt stille.
    const amount = edit.mode === "fixed" ? parseDk(edit.amount) : undefined;
    if (edit.mode === "fixed" && !(amount >= 0)) { toast.error("Skriv det faste beløb, fx 1.500 eller 1500,50"); return; }
    const lines = [];
    for (const x of edit.extra_lines) {
      const lbl = (x.label || "").trim(), amt = parseDk(x.amount);
      if (!lbl && String(x.amount ?? "").trim() === "") continue;           // tom række
      if (!lbl) { toast.error("En ekstra linje mangler tekst"); return; }
      if (!Number.isFinite(amt)) { toast.error(`"${lbl}": skriv beløbet, fx 250 eller -100`); return; }
      lines.push({ ...x, label: lbl, amount: amt });
    }
    const fact = {};
    if (edit.start_time) fact.start_time = edit.start_time;
    if (edit.end_time) fact.end_time = edit.end_time;
    if (d.original?.run_eligible) { if (edit.run_role) fact.run_role = edit.run_role; }
    else if (edit.trailer_count !== "") fact.trailer_count = Number(edit.trailer_count);
    if (edit.deduct_pause !== "") fact.deduct_pause = edit.deduct_pause === "true";
    if (edit.waiting_min !== "") fact.waiting_min = Number(edit.waiting_min);
    setSaving(true);
    if (corrMode) {
      // 2026-10-25: Faktureret tur: forskellen bliver en korrektion på næste faktura.
      try {
        const { data: c } = await axios.post(`${API}/pricing/tours/${tourId}/correction`, {
          note: edit.note, fact_overrides: fact,
          price_override: { mode: edit.mode, amount,
            add_waiting: edit.mode === "fixed" ? !!edit.add_waiting : undefined, label: edit.label || undefined,
            extra_lines: lines } });
        toast.success(`Korrektion ${fmtKr(c.delta)} - kommer på kundens næste faktura`);
        setEdit(null); setCorrMode(false); await load(); onChanged();
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setSaving(false); }
      return;
    }
    try {
      const { data } = await axios.put(`${API}/pricing/tours/${tourId}/adjust`, {
        note: edit.note, fact_overrides: fact,
        price_override: { mode: edit.mode, amount,
          add_waiting: edit.mode === "fixed" ? !!edit.add_waiting : undefined, label: edit.label || undefined,
          extra_lines: lines },
      });
      setD(data); setEdit(null); toast.success("Turen er opdateret"); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const unlock = async () => {
    const reason = window.prompt("Hvorfor fjernes markeringen? (fx markeret ved en fejl - fakturaen er ikke sendt).\nEr fakturaen sendt, så brug \"Ret med en korrektion\".");
    if (!reason || !reason.trim()) return;
    try { await axios.post(`${API}/pricing/invoice`, { tour_ids: [tourId], invoiced: false, reason }); await load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="w-full max-w-lg h-full overflow-y-auto bg-slate-50 dark:bg-slate-900 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="tour-price-drawer">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-xs text-slate-500">{d?.tour_number}{d?.external_ref ? ` · kundens ordrenr. ${d.external_ref}` : ""} · {d ? fmtDate(d.service_date) : ""}</div>
            <div className="font-bold">{d ? `${d.material} · ${d.location_name}` : "Henter..."}</div>
            <div className="text-xs text-slate-500">{d?.customer_name} · {d?.driver_name}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!d ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <div className="p-5 space-y-4">
            {d.price.locked && (
              <div className="flex items-center justify-between gap-2 p-3 rounded-xl bg-slate-200/70 dark:bg-slate-700 text-sm">
                <span className="space-y-1">
                  <span className="flex items-center gap-2"><Lock className="w-4 h-4" /> Faktureret{d.price.invoice_no ? ` (faktura ${d.price.invoice_no})` : ""}: {fmtKr(d.price.total)} - prisen er låst</span>
                  {(d.corrections || []).map(c => (
                    <span key={c.id} className="block text-xs" data-testid="drawer-correction">Korrektion {fmtKr(c.delta)} · {c.status === "open" ? <b className="text-amber-700">åben - kommer på kundens næste faktura</b> : `faktura ${c.invoice_no || "-"}`}{c.note ? ` · ${c.note}` : ""}</span>
                  ))}
                  {d.correction && <span className="block font-bold" data-testid="drawer-corrected-total">Ny pris: {fmtKr(d.correction.total)}</span>}
                </span>
                <span className="flex flex-col items-end gap-1">
                  {!edit && <button className="underline font-semibold" onClick={() => { setCorrMode(true); startEdit(); }} data-testid="price-correction">Ret med en korrektion</button>}
                  <button className="underline text-xs text-slate-500" onClick={unlock} title="Kun hvis fakturaen ikke er sendt">Fortryd markering</button>
                </span>
              </div>
            )}
            <div className={`${card} p-4`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Prisopbygning</div>
              {(d.price.lines || []).map((l, i) => (
                <div key={i} className={`flex justify-between gap-3 py-1.5 text-sm border-b border-slate-100 dark:border-slate-700 last:border-0 ${l.type === "included" ? "text-slate-400 italic" : ""}`}>
                  <span className={l.type === "surcharge" ? "text-amber-700 dark:text-amber-300" : l.type === "extra" || l.type === "override" ? "text-blue-700 dark:text-blue-300" : ""}>{l.label}</span>
                  <span className="font-mono">{fmtKr(l.amount)}</span>
                </div>
              ))}
              {d.price.why?.text && <div className="mt-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-xs text-red-800 dark:text-red-200" data-testid="price-why"><b>Hvorfor ingen pris?</b> {d.price.why.text}</div>}
              <div className="flex justify-between pt-2 mt-1 font-bold"><span>I alt ekskl. moms</span><span>{fmtKr(d.price.total)}</span></div>
              {(d.price.flags || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">{d.price.flags.map(f => <span key={f} className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-slate-700">{f}</span>)}</div>
              )}
              {d.price.base_rule && <div className="text-xs text-slate-500 mt-2">Grundpris fra reglen "{d.price.base_rule.name}"</div>}
            </div>

            {d.price.time && (
              <div className={`${card} p-4 text-sm`}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Tid</div>
                <Row k="På vej → afsluttet" v={fmtMin(d.price.time.gross_min)} />
                {d.price.time.shared_min > 0 && <Row k="Delt med samtidige ture" v={`− ${fmtMin(d.price.time.shared_min)}`} />}
                <Row k={`Pause under turen ${d.price.time.deduct_pause ? "(trækkes fra)" : "(tæller med)"}`} v={d.price.time.pause_min ? `${d.price.time.deduct_pause ? "− " : ""}${fmtMin(d.price.time.pause_min)}` : "-"} />
                <Row k={`Faktureret tid · ${d.price.time.rounding}`} v={<b>{fmtMin(d.price.time.billable_min)}</b>} />
              </div>
            )}

            <div className={`${card} p-4 text-sm`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Fakta</div>
              <Row k="Start – slut" v={`${d.start_time || "-"} – ${d.end_time || "-"}`} />
              <Row k="Dagtype" v={`${options.day_types[d.day_type]} · ${d.holiday_source}`} />
              <Row k="Opgavetype" v={options.task_types[d.task_type] || d.task_type} />
              <Row k="Prioritet" v={options.priorities[d.priority] || d.priority} />
              {d.run_role ? (
                <Row k="Kørsel" v={<span data-testid="price-run-role">{d.run_alone ? runText(d) : (RUN_LABEL[d.run_role] || d.run_role)}{(d.run_partners || []).length ? ` · sammen med ${partnerText(d.run_partners)}` : ""}</span>} />
              ) : <Row k="Hænger" v={d.trailer ? "Med hænger" : "Uden hænger"} />}
              <Row k="Ventetid" v={d.waiting_min ? `${d.waiting_min} min` : "-"} />
              <Row k="Leveringsadresse" v={d.delivery_label || "-"} />
              {d.container_label && <Row k="Containerstørrelse" v={d.container_label} />}
              {d.outcome === "not_completed" && <Row k="Udfald" v={<span className="text-red-600 font-semibold">Forgæves</span>} />}
            </div>

            {!d.price.locked && !edit && (
              <button className={`${btnGhost} w-full justify-center`} onClick={startEdit}><Edit2 className="w-4 h-4" /> Tilpas tur eller pris</button>
            )}
            {edit && (
              <div className={`${card} p-4 space-y-3`}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{corrMode ? "Korrektion af faktureret tur - forskellen kommer på næste faktura" : "Tilpas"}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className={label}>Start</span><input type="time" className={input} value={edit.start_time} placeholder={d.original.start_time} onChange={e => setEdit({ ...edit, start_time: e.target.value })} /></div>
                  <div><span className={label}>Slut</span><input type="time" className={input} value={edit.end_time} onChange={e => setEdit({ ...edit, end_time: e.target.value })} /></div>
                  {d.original?.run_eligible ? (
                    <div><span className={label}>Kørsel</span>
                      <select className={input} value={edit.run_role} onChange={e => setEdit({ ...edit, run_role: e.target.value })} data-testid="adjust-run-role">
                        <option value="">Som kørt ({RUN_LABEL[d.original?.run_role] || "ikke registreret"})</option>
                        {Object.entries(RUN_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select></div>
                  ) : (
                    <div><span className={label}>Hængere</span>
                      <select className={input} value={edit.trailer_count} onChange={e => setEdit({ ...edit, trailer_count: e.target.value })} data-testid="adjust-trailers">
                        <option value="">Som registreret ({d.original?.trailer_count ?? (d.trailer ? 1 : 0)})</option>
                        {[0, 1, 2, 3, 4].map(n => <option key={n} value={String(n)}>{n === 0 ? "Uden hænger" : `${n} hænger${n === 1 ? "" : "e"}`}</option>)}
                      </select></div>
                  )}
                  <div><span className={label}>Ventetid (min)</span><input type="number" min="0" className={input} value={edit.waiting_min} onChange={e => setEdit({ ...edit, waiting_min: e.target.value })} /></div>
                  <div className="col-span-2"><span className={label}>Pause under turen</span>
                    <select className={input} value={edit.deduct_pause} onChange={e => setEdit({ ...edit, deduct_pause: e.target.value })}>
                      <option value="">Som prisreglen siger</option><option value="true">Træk pause fra</option><option value="false">Pause tæller med</option>
                    </select></div>
                </div>
                <div>
                  <span className={label}>Pris</span>
                  <Segmented value={edit.mode} onChange={v => setEdit({ ...edit, mode: v })} options={[{ value: "rules", label: "Beregn efter regler" }, { value: "fixed", label: "Fast beløb" }]} />
                  {edit.mode === "fixed" && <input inputMode="decimal" className={`${input} mt-2`} placeholder="Beløb ekskl. moms, fx 1.500" value={edit.amount} onChange={e => setEdit({ ...edit, amount: e.target.value })} data-testid="adjust-fixed-amount" />}
                  {edit.mode === "fixed" && (
                    <label className="mt-2 flex items-center gap-2 text-sm">
                      <input type="checkbox" className="accent-emerald-600 w-4 h-4" checked={!!edit.add_waiting} onChange={e => setEdit({ ...edit, add_waiting: e.target.checked })} />
                      Læg ventetid oveni (efter ventetidsreglen)
                    </label>
                  )}
                </div>
                <div>
                  <span className={label}>Ekstra linjer</span>
                  {edit.extra_lines.map((x, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input className={input} placeholder="Fx Ekstra rengøring" value={x.label} onChange={e => { const l = [...edit.extra_lines]; l[i] = { ...x, label: e.target.value }; setEdit({ ...edit, extra_lines: l }); }} />
                      <input inputMode="decimal" className={`${inputFit} w-32`} placeholder="kr" value={x.amount} onChange={e => { const l = [...edit.extra_lines]; l[i] = { ...x, amount: e.target.value }; setEdit({ ...edit, extra_lines: l }); }} />
                      <button className="p-2 text-slate-400 hover:text-red-600" onClick={() => setEdit({ ...edit, extra_lines: edit.extra_lines.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button className="text-xs font-semibold text-emerald-700 dark:text-emerald-300" onClick={() => setEdit({ ...edit, extra_lines: [...edit.extra_lines, { label: "", amount: "" }] })}>+ Tilføj linje</button>
                </div>
                <div><span className={label}>Begrundelse (påkrævet)</span><input className={input} value={edit.note} onChange={e => setEdit({ ...edit, note: e.target.value })} placeholder="Fx aftalt med kunden pr. telefon" /></div>
                <div className="flex gap-2 justify-end">
                  <button className={btnGhost} onClick={() => { setEdit(null); setCorrMode(false); }}>Annuller</button>
                  <button className={btnPrimary} disabled={saving} onClick={save}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem</button>
                </div>
              </div>
            )}

            {(d.requests?.length > 0 || d.history?.length > 0) && (
              <div className={`${card} p-4 text-sm space-y-2`}>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Historik</div>
                {d.requests.map(r => (
                  <div key={r.id} className="text-xs border-l-2 border-amber-400 pl-2">
                    <b>{r.driver_name}</b> anmodede om rettelse · {new Date(r.created_at).toLocaleString("da-DK")} · <b>{{ pending: "Afventer", approved: "Godkendt", rejected: "Afvist" }[r.status]}</b>
                    <div className="text-slate-500">"{r.comment}"</div>
                  </div>
                ))}
                {[...d.history].reverse().map((h, i) => (
                  <div key={i} className="text-xs border-l-2 border-blue-400 pl-2">
                    <b>{h.by}</b> · {new Date(h.at).toLocaleString("da-DK")}{h.kind === "correction" && h.delta != null ? <> · <b>korrektion {fmtKr(h.delta)}</b></> : null}<div className="text-slate-500">{h.note}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-slate-100 dark:border-slate-700 last:border-0">
    <span className="text-slate-500">{k}</span><span className="text-right">{v}</span>
  </div>
);

/* ======================================================================
   Prisregler
   ====================================================================== */
const EMPTY_COND = { customer_ids: [], location_codes: [], delivery_labels: [], materials: [], task_types: [], priorities: [], day_types: [], container_labels: [], trailer: "", trailer_counts: [], time_from: "", time_to: "", outcome: "completed", run_roles: [] };

function ruleSummary(rule, options) {
  if (rule.kind === "base") {
    const p = rule.pricing || {};
    if (p.model === "fixed") return `Fast pris ${fmtKr(p.amount)}`;
    if (p.model === "per_container") return `${fmtKr(p.amount)} pr. container`;
    if (p.model === "per_unit") return `${fmtKr(p.amount)} pr. ${p.unit || "enhed"}${p.min_amount ? ` · min. ${fmtKr(p.min_amount)}` : ""}`;
    const parts = [`${fmtKr(p.hourly_rate)}/t`];
    if (p.start_fee) parts.push(`start ${fmtKr(p.start_fee)}`);
    if (p.min_minutes) parts.push(`min. ${p.min_minutes} min`);
    if (p.min_amount) parts.push(`min. ${fmtKr(p.min_amount)}`);
    if (p.rounding_mode !== "none") parts.push(`${{ up: "op", down: "ned", nearest: "nærmeste" }[p.rounding_mode]} til ${p.rounding_minutes} min`);
    parts.push(p.deduct_pause ? "pause trækkes fra" : "pause tæller med");
    return `Timepris ${parts.join(" · ")}`;
  }
  const s = rule.surcharge || {};
  const v = s.unit === "percent" ? `+${s.value}%` : s.unit === "per_hour" ? `+${fmtKr(s.value)}/t` : `+${fmtKr(s.value)}`;
  return `${options.categories[rule.category] || "Tillæg"} ${v}`;
}

function conditionChips(c, options) {
  const out = [];
  const nameOf = (list, key = "id", nameKey = "name") => (v) => (list.find(x => x[key] === v) || {})[nameKey] || v;
  if (c.customer_ids?.length) out.push(c.customer_ids.map(nameOf(options.customers)).join(", "));
  if (c.location_codes?.length) out.push(c.location_codes.map(nameOf(options.locations, "code")).join(", "));
  if (c.delivery_labels?.length) out.push("→ " + c.delivery_labels.join(", "));
  if (c.materials?.length) out.push(c.materials.join(", "));
  if (c.task_types?.length) out.push(c.task_types.map(t => options.task_types[t] || t).join(", "));
  if (c.priorities?.length) out.push(c.priorities.map(p => options.priorities[p]).join(", "));
  if (c.day_types?.length) out.push(c.day_types.map(d => options.day_types[d]).join(", "));
  if (c.trailer) out.push(c.trailer === "with" ? "Med hænger" : "Uden hænger");
  if ((c.trailer_counts || []).length) out.push(`${c.trailer_counts.map(n => (Number(n) >= 3 ? "3+" : n)).join("/")} hænger${c.trailer_counts.length === 1 && Number(c.trailer_counts[0]) === 1 ? "" : "e"}`);
  if (c.container_labels?.length) out.push(c.container_labels.join(", "));
  if (c.time_from && c.time_to) out.push(`Start ${c.time_from}–${c.time_to}`);
  if ((c.run_roles || []).length) out.push(c.run_roles.map(r => RUN_LABEL[r] || r).join(", "));
  if (c.outcome === "not_completed") out.push("Forgæves ture");
  if (c.outcome === "any") out.push("Gennemført + forgæves");
  return out;
}

/** 2026-10-17: Tillæg hvis type ikke matcher betingelserne gælder ALLE ture
    (fx et "weekendtillæg" uden lør/søn). Vises som advarsel på reglen. */
function riskWarning(r) {
  if (r.kind !== "surcharge") return "";
  const c = r.conditions || {};
  const has = (k, v) => (c[k] || []).includes(v);
  if (r.category === "weekend" && !(has("day_types", "saturday") || has("day_types", "sunday"))) return "Gælder alle dage - mangler dagtype lørdag/søndag";
  if (r.category === "holiday" && !has("day_types", "holiday")) return "Gælder alle dage - mangler dagtype helligdag";
  if (r.category === "haster" && !has("priorities", "haster")) return "Gælder alle ture - mangler prioritet Haster";
  if (r.category === "trailer" && !(c.trailer === "with" || (c.trailer_counts || []).length)) return "Gælder også ture uden hænger";
  if (r.category === "evening" && !(c.time_from && c.time_to)) return "Gælder hele døgnet - mangler tidsrum";
  return "";
}

function RulesView({ options, reloadOptions, canEdit = true, initialFilter = "all" }) {
  const [rules, setRules] = useState(null);
  const [history, setHistory] = useState({});
  const [filter, setFilter] = useState(initialFilter);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [regulate, setRegulate] = useState(false);
  const [version, setVersion] = useState(0);
  const [importing, setImporting] = useState(false);   // 2026-10-25: prisaftaler fra Excel

  const load = useCallback(async () => {
    try { setRules((await axios.get(`${API}/pricing/rules`)).data); setVersion(v => v + 1); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const importLegacy = async () => {
    try {
      const { data } = await axios.post(`${API}/pricing/import-legacy`);
      toast.success(`${data.created} regler importeret fra den gamle prisliste`);
      // 2026-10-28: En pris for en plads, I ikke kører på (eller med et navn, der findes to gange), kommer ikke med.
      if (data.skipped?.length) toast.warning(`Ikke importeret (pladsen findes ikke hos jer): ${data.skipped.join(", ")}`, { duration: 10000 });
      load(); reloadOptions();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // 2026-10-17: Standard er at reglen ophører fra nu - eksisterende ture
  // beholder deres pris. "Slet helt" fjerner den også bagudrettet.
  const remove = async (id, effective = "now") => {
    try {
      await axios.delete(`${API}/pricing/rules/${id}`, { params: { effective } });
      toast.success(effective === "all" ? "Regel slettet helt" : "Reglen gælder ikke for ture kørt fra i dag");
      setConfirmDel(null); load();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleHistory = async (r) => {
    if (history[r.id]) { setHistory(h => ({ ...h, [r.id]: null })); return; }
    try { const { data } = await axios.get(`${API}/pricing/rules/${r.id}/history`); setHistory(h => ({ ...h, [r.id]: data })); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (r) => {
    try { await axios.put(`${API}/pricing/rules/${r.id}`, { ...r, active: !r.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!rules) return <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const visible = rules.filter(r => {
    if (filter === "all") return true;
    if (filter === "general") return !(r.conditions?.customer_ids || []).length;
    if (filter === "own") {
      const ownIds = new Set(options.customers.filter(c => c.own).map(c => c.id));
      return (r.conditions?.customer_ids || []).some(id => ownIds.has(id));
    }
    return (r.conditions?.customer_ids || []).includes(filter);
  });
  // Prisaftaler (grundpris + tillæg) vises samlet; ældre enkeltregler for sig.
  const bundles = Object.values(visible.filter(r => r.bundle_id).reduce((m, r) => {
    (m[r.bundle_id] = m[r.bundle_id] || []).push(r); return m;
  }, {})).sort((a, b) => (a[0].bundle_name || "").localeCompare(b[0].bundle_name || "", "da"));
  // 2026-10-25: Aftaler, der er udløbet (fx afløst af en prisregulering), står
  // samlet nederst - ellers blev listen længere for hver regulering.
  const todayIso = localDate();
  const expired = bundles.filter(rs => rs.every(r => r.valid_to && r.valid_to < todayIso));
  const live = bundles.filter(rs => !expired.includes(rs));
  const singles = visible.filter(r => !r.bundle_id);
  const base = singles.filter(r => r.kind === "base");
  const sur = singles.filter(r => r.kind === "surcharge");
  const removeBundle = async (bid, effective) => {
    try {
      await axios.delete(`${API}/pricing/bundles/${bid}`, { params: { effective } });
      toast.success(effective === "all" ? "Prisaftalen er slettet helt" : "Prisaftalen gælder ikke for ture kørt fra i dag");
      setConfirmDel(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const BundleCard = ({ rs }) => {
    const bb = bundleFromRules(rs);
    const baseRule = rs.find(r => r.kind === "base" && !isFailedRule(r) && !isRunPart(r));
    const failedRule = rs.find(isFailedRule);
    const runParts = rs.filter(isRunPart);
    const surs = rs.filter(r => r.kind === "surcharge");
    const chips = conditionChips(bb.conditions, options);
    const vf = (baseRule || rs[0]).version_from;
    return (
      <div className={`${card} p-4 flex flex-col sm:flex-row sm:items-start gap-3 ${bb.active ? "" : "opacity-60"}`} data-testid={`bundle-card-${bb.bundle_id}`}>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold">{bb.name}</span>
            {bb.code && <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-mono" title="Aftale-ID (Excel)">{bb.code}</span>}
            <InfoTip text="Prisaftale: grundpris og tillæg for de samme ture." own={bb.note} />
            {!bb.active && <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-200 dark:bg-slate-700">Inaktiv</span>}
            {vf && new Date(vf) > new Date() && <span className="px-2 py-0.5 rounded-full text-[11px] bg-violet-100 text-violet-800">Træder i kraft {fmtTs(vf)}</span>}
            {(bb.valid_from || bb.valid_to) && <span className="px-2 py-0.5 rounded-full text-[11px] bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" title="Kørselsdato">{bb.valid_from && bb.valid_to ? `Kørt ${fmtDate(bb.valid_from)} - ${fmtDate(bb.valid_to)}` : bb.valid_from ? `Kørt fra ${fmtDate(bb.valid_from)}` : `Kørt til og med ${fmtDate(bb.valid_to)}`}</span>}
          </div>
          <div className="flex flex-wrap gap-1">
            {chips.map((x, i) => <span key={i} className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">{x}</span>)}
            {chips.length === 0 && <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-slate-700 text-slate-500">Gælder alle ture</span>}
          </div>
          <div className="text-sm space-y-0.5">
            {baseRule ? <div><span className="text-slate-500">Grundpris:</span> <b className="text-emerald-700 dark:text-emerald-300">{ruleSummary(baseRule, options)}</b></div>
              : <div className="text-slate-500">Ingen grundpris (bruger den generelle)</div>}
            {failedRule && <div><span className="text-slate-500">Forgæves kørsel:</span> <b>{fmtKr(failedRule.pricing?.amount)}</b></div>}
            {runParts.map(r => (
              <div key={r.id} data-testid={`bundle-card-run-${r.conditions.run_roles[0]}`}><span className="text-slate-500">{r.conditions.run_roles[0] === "trailer" ? "Container på hængeren" : "Container på bilen (hængerkørsel)"}:</span>{" "}
                <b>{r.run_spec?.mode === "percent" ? `${String(r.run_spec.value).replace(".", ",")} % af grundprisen (${ruleSummary(r, options)})` : ruleSummary(r, options)}</b></div>
            ))}
            {surs.map(r => (
              <div key={r.id}><span className="text-slate-500">{options.categories[r.category]}:</span> <b>{Number(r.surcharge?.value) === 0 ? "Fritaget" : ruleSummary(r, options)}</b>
                {r.category === "stop" && Number(r.surcharge?.value) !== 0 && <span className="text-slate-500"> pr. stop ud over {r.surcharge?.included ?? 2}</span>}</div>
            ))}
          </div>
          {vf && <div className="text-[11px] text-slate-500">Gælder for ture oprettet fra {fmtTs(vf)}</div>}
        </div>
        {canEdit && <div className="flex items-center gap-1 shrink-0">
          <button title="Rediger" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => setEditing(bb)} data-testid="bundle-edit"><Edit2 className="w-4 h-4" /></button>
          <button title="Kopiér (fx til en anden kunde)" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => setEditing(copyBundle(bb))}><Copy className="w-4 h-4" /></button>
          {confirmDel === bb.bundle_id ? (
            <div className="flex flex-col sm:flex-row gap-1">
              <button className="px-2 py-1 rounded-lg bg-amber-600 text-white text-xs font-bold" title="Ture kørt fra i dag får ikke længere prisen. Kørte ture beholder den." onClick={() => removeBundle(bb.bundle_id, "today")}>Stop fra i dag</button>
              <button className="px-2 py-1 rounded-lg bg-red-600 text-white text-xs font-bold" onClick={() => removeBundle(bb.bundle_id, "all")}>Slet helt</button>
              <button className="px-2 py-1 rounded-lg text-xs" onClick={() => setConfirmDel(null)}>Annuller</button>
            </div>
          ) : <button title="Slet" className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600" onClick={() => setConfirmDel(bb.bundle_id)}><Trash2 className="w-4 h-4" /></button>}
        </div>}
      </div>
    );
  };

  const RuleCard = ({ r }) => (
    <div className={`${card} p-4 flex flex-col sm:flex-row sm:items-start gap-3 ${r.active ? "" : "opacity-60"}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold">{r.name}</span>
          <InfoTip text={ruleHelp(r)} own={r.note} />
          {!r.active && <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-200 dark:bg-slate-700">Inaktiv</span>}
          {(r.valid_from || r.valid_to) && <span className="px-2 py-0.5 rounded-full text-[11px] bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" title="Kørselsdato">Kørt {r.valid_from || "…"} → {r.valid_to || "…"}</span>}
          {r.version_from && new Date(r.version_from) > new Date() && <span className="px-2 py-0.5 rounded-full text-[11px] bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">Træder i kraft {fmtTs(r.version_from)}</span>}
          {r.version_to && <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Ophører {fmtTs(r.version_to)}</span>}
          {riskWarning(r) && <span className="px-2 py-0.5 rounded-full text-[11px] bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200 inline-flex items-center gap-1" data-testid="rule-risk"><AlertTriangle className="w-3 h-3" />{riskWarning(r)}</span>}
        </div>
        {(r.version_from || r.versions > 1) && (
          <div className="text-[11px] text-slate-500 mt-0.5">
            {r.version_from ? `Gælder for ture oprettet fra ${fmtTs(r.version_from)}` : "Gælder for alle ture"}
            {r.versions > 1 && <> · <button className="underline" onClick={() => toggleHistory(r)}>{history[r.id] ? "Skjul" : "Vis"} {r.versions - 1} tidligere version{r.versions - 1 === 1 ? "" : "er"}</button></>}
          </div>
        )}
        {history[r.id] && (
          <ul className="mt-2 space-y-1 border-l-2 border-slate-200 dark:border-slate-700 pl-3">
            {history[r.id].filter(v => v.id !== r.id).map(v => (
              <li key={v.id} className="text-xs text-slate-600 dark:text-slate-300">
                <span className="font-semibold">{ruleSummary(v, options)}</span>
                <span className="text-slate-400"> · ture oprettet {v.version_from ? `fra ${fmtTs(v.version_from)}` : "før"} {v.version_to ? `til ${fmtTs(v.version_to)}` : ""}{v.updated_by ? ` · ${v.updated_by}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="text-sm text-emerald-700 dark:text-emerald-300 font-semibold mt-0.5">{ruleSummary(r, options)}</div>
        <div className="flex flex-wrap gap-1 mt-2">
          {conditionChips(r.conditions || {}, options).map((c, i) => <span key={i} className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">{c}</span>)}
          {conditionChips(r.conditions || {}, options).length === 0 && <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-slate-700 text-slate-500">Gælder alle ture</span>}
        </div>
      </div>
      {canEdit && <div className="flex items-center gap-1 shrink-0">
        <button title={r.active ? "Deaktivér" : "Aktivér"} onClick={() => toggleActive(r)} className={`w-10 h-6 rounded-full relative transition-colors ${r.active ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${r.active ? "left-[18px]" : "left-0.5"}`} />
        </button>
        <button title="Rediger (åbner som prisaftale)" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => setEditing(bundleFromRules([r]))}><Edit2 className="w-4 h-4" /></button>
        <button title="Kopiér" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => setEditing(copyBundle(bundleFromRules([r])))}><Copy className="w-4 h-4" /></button>
        {confirmDel === r.id ? (
          <div className="flex flex-col sm:flex-row gap-1">
            <button className="px-2 py-1 rounded-lg bg-amber-600 text-white text-xs font-bold" title="Ture kørt fra i dag får ikke længere prisen. Kørte ture beholder den." onClick={() => remove(r.id, "today")}>Stop fra i dag</button>
            <button className="px-2 py-1 rounded-lg bg-red-600 text-white text-xs font-bold" title="Fjernes også fra eksisterende ture der ikke er faktureret" onClick={() => remove(r.id, "all")}>Slet helt</button>
            <button className="px-2 py-1 rounded-lg text-xs" onClick={() => setConfirmDel(null)}>Annuller</button>
          </div>
        ) : <button title="Slet" className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600" onClick={() => setConfirmDel(r.id)}><Trash2 className="w-4 h-4" /></button>}
      </div>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className={`${card} p-4 flex flex-wrap items-center justify-between gap-3`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">Vis regler for</span>
          <select className={`${inputFit} w-auto min-w-[220px]`} value={filter} onChange={e => setFilter(e.target.value)} data-testid="rules-filter">
            <option value="all">Alle regler</option>
            <option value="general">Generelle (gælder alle kunder)</option>
            {options.customers.some(c => c.own) && <option value="own">Alle egne kunder</option>}
            {options.customers.some(c => !c.own) && (
              <optgroup label="Genbrugsplads-kunder">
                {options.customers.filter(c => !c.own).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            )}
            {options.customers.some(c => c.own) && (
              <optgroup label="Egne kunder">
                {options.customers.filter(c => c.own).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        {canEdit && <div className="flex flex-wrap gap-2">
          <button className={btnGhost} data-testid="regulate-open" onClick={() => setRegulate(true)}
            title="Hæv eller sænk priserne i flere aftaler med en procent fra en dato (fx 1. januar)">
            <TrendingUp className="w-4 h-4" /> Prisregulering
          </button>
          <button className={btnGhost} data-testid="bundle-import" onClick={() => setImporting(true)}
            title="Opret mange prisaftaler fra Excel (én række = én aftale)"><FileSpreadsheet className="w-4 h-4" /> Importér fra Excel</button>
          <button className={btnPrimary} data-testid="bundle-new" onClick={() => setEditing(newBundle({ customer_ids: !["all", "general", "own"].includes(filter) ? [filter] : [] }))}>
            <Plus className="w-4 h-4" /> Ny prisaftale
          </button>
        </div>}
        {importing && <ImportDialog kind="prices" title="Importér prisaftaler" onClose={() => setImporting(false)} onDone={() => { load(); reloadOptions(); }} />}
      </div>

      <div className="flex gap-2 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-sm text-sky-900 dark:text-sky-100">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <b>Sådan virker det:</b> En <b>prisaftale</b> samler grundpris og tillæg for de samme ture (fx en kunde). Hver tur får én grundpris - den mest specifikke aftale vinder (kunde &gt; lokation &gt; leveringsadresse &gt; opgavetype &gt; materiale).
          Tillæg lægges oveni - højst ét pr. type, igen det mest specifikke. "Fritaget" på en kundes aftale fjerner et generelt tillæg for den kunde. Brug <b>Prøv prisen</b> i editoren før du gemmer.
          <div className="mt-1"><b>Særpris for en del af en kunde</b> (fx en lokation, et materiale eller containeren på hængeren): lav en aftale for kunden + det ene, der er anderledes - med kun den pris, der afviger. Resten kommer fra kundens almindelige aftale. <b>Regeltjek</b> nedenfor viser, hvis to aftaler krydser hinanden.</div>
        </div>
      </div>

      <RuleCheck reloadKey={version} onOpen={(f, which) => {
        const bid = which === "loser" ? f.loser_bundle : f.winner_bundle;
        const group = which === "loser" ? f.loser_group : f.winner_group;
        const rs = rules.filter(r => (bid && r.bundle_id === bid) || (!bid && (r.version_group || r.id) === group));
        if (rs.length) setEditing(bundleFromRules(rs));
      }} canEdit={canEdit} />

      {rules.length === 0 && options.legacy_prices > 0 && (
        <div className={`${card} p-4 flex flex-wrap items-center justify-between gap-3`}>
          <span className="text-sm">Du har {options.legacy_prices} priser fra den gamle prisliste. Vil du importere dem som regler?</span>
          <button className={btnGhost} onClick={importLegacy}>Importér gamle priser</button>
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-bold text-slate-600 dark:text-slate-300">Prisaftaler ({live.length})</h3>
        {bundles.length === 0 && <div className={`${card} p-6 text-sm text-slate-500 text-center`}>Ingen prisaftaler endnu. Start med en generel aftale uden betingelser - så har alle ture en pris - og læg tillæg på samme sted.</div>}
        {live.map(rs => <BundleCard key={rs[0].bundle_id} rs={rs} />)}
      </div>
      {expired.length > 0 && (
        <details className="space-y-2" data-testid="bundles-expired">
          <summary className="text-sm font-bold text-slate-600 dark:text-slate-300 cursor-pointer">Udløbne prisaftaler ({expired.length}) <span className="font-normal text-slate-500">- gælder kun ture kørt før deres slutdato</span></summary>
          <div className="space-y-2 mt-2">{expired.map(rs => <BundleCard key={rs[0].bundle_id} rs={rs} />)}</div>
        </details>
      )}
      {singles.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-slate-600 dark:text-slate-300">Enkeltregler ({singles.length})</h3>
          <p className="text-xs text-slate-500">Regler oprettet før prisaftaler fandtes. Tryk ret for at gøre en regel til en prisaftale.</p>
          {[...base, ...sur].map(r => <RuleCard key={r.id} r={r} />)}
        </div>
      )}
      {editing && <BundleEditor bundle={editing} options={options} firstRule={rules.length === 0} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {regulate && <RegulateModal rules={rules} options={options} onClose={() => setRegulate(false)} onDone={() => { setRegulate(false); load(); }} />}
    </div>
  );
}

/* ======================================================================
   Kunder & priser (2026-10-17): ét sted at se alle kunder - genbrugsplads-
   kunder og logistikfirmaets egne kunder - og hvilke priser de har.
   ====================================================================== */
const EMPTY_CUST = { name: "", cvr: "", address: "", contact: "", phone: "", email: "", note: "" };

/** 2026-10-26: Kundens steder (byggepladser, lagre ...) - "Ny tur" udfylder afhentning/aflevering herfra. */
export function CustomerAddresses({ value, onChange }) {
  const blank = { label: "", street: "", postal: "", city: "", open_from: "", open_to: "", contact_name: "", contact_phone: "", note: "", weekly_hours: null };
  const [edit, setEdit] = useState(null);   // index | "new"
  const [d, setD] = useState(blank);
  // 2026-10-28: Mange adresser (fx en kæde med 99 butikker) - søg i listen; mange på én gang med Excel (Kunder › Adresser).
  const [q, setQ] = useState("");
  const nq = q.trim().toLowerCase();
  const rows = value.map((a, i) => [a, i]).filter(([a]) => !nq || `${a.label || ""} ${formatAddress(a)}`.toLowerCase().includes(nq));
  // 2026-10-28: Kun én måde at skrive tider på (pr. ugedag) - en ældre adresse med ét tidsrum vises som det tidsrum alle dage.
  const start = (i) => { setEdit(i); setD(i === "new" ? blank : { ...blank, ...value[i], weekly_hours: weekFromRange(value[i]), open_from: "", open_to: "" }); };
  const keep = () => {
    if (!(d.street || "").trim() && !(d.city || "").trim()) { toast.error("Skriv adressen"); return; }
    onChange(edit === "new" ? [...value, d] : value.map((a, i) => (i === edit ? d : a)));
    setEdit(null);
  };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="customer-addresses">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold">Kundens adresser <span className="font-normal text-slate-500">({value.length})</span></span>
        {edit === null && <button type="button" className={btnGhost} onClick={() => start("new")} data-testid="customer-address-add"><Plus className="w-4 h-4" /> Tilføj adresse</button>}
      </div>
      <p className="text-[11px] text-slate-500">Byggepladser, butikker, lagre o.l. Vælges under Afhentning/Aflevering i Ny tur. Gemmes sammen med kunden - mange på én gang med Excel (Kunder › Adresser).</p>
      {value.length > 10 && <input className={input} value={q} onChange={e => setQ(e.target.value)} placeholder={`Søg blandt ${value.length} adresser`} data-testid="customer-address-search" />}
      {rows.map(([a, i]) => edit === i ? null : (
        <div key={a.id || i} className="flex items-center gap-2 text-sm rounded-lg bg-slate-50 dark:bg-slate-900 px-3 py-2">
          <div className="min-w-0 flex-1"><span className="font-semibold">{a.label || a.street}</span>
            <span className="text-xs text-slate-500"> · {formatAddress(a)}{a.weekly_hours ? ` · ${weekSummary(a.weekly_hours)}` : a.open_from && a.open_to ? ` · ${a.open_from}–${a.open_to}` : ""}{a.contact_name ? ` · ${a.contact_name}` : ""}</span></div>
          <button type="button" className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700" onClick={() => start(i)} aria-label="Ret"><Edit2 className="w-4 h-4" /></button>
          <button type="button" className="p-1.5 rounded hover:bg-red-50 text-red-600" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Fjern"><X className="w-4 h-4" /></button>
        </div>
      ))}
      {edit !== null && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 p-3 space-y-2" data-testid="customer-address-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><span className={label}>Navn</span><input className={input} value={d.label} onChange={e => setD({ ...d, label: e.target.value })} placeholder="Fx Byggeplads Carlsberg" data-testid="customer-address-label" /></div>
            <AddressField label="Adresse" value={d} onChange={(v) => setD({ ...d, ...v })} inputCls={input} testid="customer-address-addr" />
            <div className="grid grid-cols-2 gap-2">
              <div><span className={label}>Kontakt</span><input className={input} value={d.contact_name} onChange={e => setD({ ...d, contact_name: e.target.value })} placeholder="Valgfri" /></div>
              <div><span className={label}>Telefon</span><input className={input} value={d.contact_phone} inputMode="tel" onChange={e => setD({ ...d, contact_phone: e.target.value })} placeholder="Valgfri" /></div>
            </div>
            <div className="sm:col-span-2"><span className={label}>Note til chaufføren</span><input className={input} value={d.note} onChange={e => setD({ ...d, note: e.target.value })} placeholder="Fx port 3, ring ved ankomst" /></div>
            {/* 2026-10-27: Stedets åbningstider pr. dag - Ny tur viser dagens tider og advarer, hvis der er lukket. */}
            <div className="sm:col-span-2"><span className={label}>Stedets åbningstider (valgfri)</span><WeeklyHours value={d.weekly_hours} onChange={(w) => setD({ ...d, weekly_hours: w })} testid="customer-address-week" /></div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className={btnGhost} onClick={() => setEdit(null)}>Annuller</button>
            <button type="button" className={btnPrimary} onClick={keep} data-testid="customer-address-keep"><Check className="w-4 h-4" /> {edit === "new" ? "Tilføj" : "Ret"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 2026-10-27: En egen kunde bruger nu selv ContainerFlow - prisaftalerne flyttes til kunden fra en dato. */
function MoveToCompany({ reg, customers, onClose, onDone }) {
  const [target, setTarget] = useState("");
  const [day, setDay] = useState(() => localDate());
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/logistics/customers/${reg.id}/move-to-company`, { company_id: target, from_date: day, dry_run: dry });
      if (dry) setPlan(data); else { toast.success(`${data.moved} prisaftale${data.moved === 1 ? "" : "r"} flyttet til ${data.customer} fra ${day}`); onDone(); }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return createPortal(
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-lg p-5 space-y-3`} onClick={e => e.stopPropagation()} data-testid="move-to-company">
        <h3 className="font-bold text-lg">Flyt {reg.name} til en kunde med pladser</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300">Når kunden bruger ContainerFlow, eller I har oprettet den som egen kunde med pladser: prisaftalerne flyttes fra en kørselsdato (de gamle gælder ture kørt før), og kundens ture fra datoen følger med. Så har kunden ét sæt priser. {reg.name} skjules i Ny tur - historikken bevares.</p>
        <div><span className={label}>Kunden i ContainerFlow</span>
          <select className={input} value={target} onChange={e => { setTarget(e.target.value); setPlan(null); }} data-testid="move-target">
            <option value="">Vælg…</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div className="max-w-xs"><span className={label}>Ture kørt fra og med</span><input type="date" className={input} value={day} onChange={e => { setDay(e.target.value); setPlan(null); }} /></div>
        {plan && (
          <div className="rounded-lg bg-slate-50 dark:bg-slate-900 p-3 text-sm space-y-1" data-testid="move-plan">
            <div>{plan.agreements.length} prisaftale{plan.agreements.length === 1 ? "" : "r"} flyttes til {plan.customer}:</div>
            {plan.agreements.map(a => <div key={a.code + a.name} className="text-xs">• {a.name}{a.code ? ` (${a.code})` : ""}{a.warning && <span className="text-amber-700"> - {a.warning}</span>}</div>)}
            {plan.open_own_tours > 0 && <div className="text-xs text-amber-700">{plan.open_own_tours} planlagte egne ture fra datoen står stadig på {reg.name} - bestil dem på kundens pladser i stedet.</div>}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose}>Annuller</button>
          {!plan ? <button className={btnPrimary} disabled={!target || busy} onClick={() => run(true)} data-testid="move-preview">Se hvad der flyttes</button>
            : <button className={btnPrimary} disabled={busy} onClick={() => run(false)} data-testid="move-run">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Flyt priserne</button>}
        </div>
      </div>
    </div>, document.body);
}

export function CustomerForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState({ ...EMPTY_CUST, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  // 2026-10-27: Har kunden genbrugspladser med containere (fx ARGO), oprettes den som
  // "egen kunde med pladser" - samme opbygning som en kunde i ContainerFlow.
  const [withSites, setWithSites] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Skriv kundens navn"); return; }
    setBusy(true);
    try {
      if (!initial?.id && withSites) {
        // 2026-10-27: Kunder med pladser oprettes af ContainerFlow (én gang pr. kunde) - her sendes en anmodning.
        await requestExternal({ kind: "customer", customer_name: f.name.trim(), note: f.note || "" });
        toast.success(`Anmodning sendt: ContainerFlow opretter ${f.name.trim()} med pladserne og sætter jer på som vognmand`);
      } else if (initial?.id) {
        await axios.put(`${API}/logistics/customers/${initial.id}`, f);
        toast.success("Kunden er opdateret");
      } else {
        await axios.post(`${API}/logistics/customers`, f);
        toast.success("Kunden er oprettet");
      }
      onSaved();
    } catch (e) { if (e !== "cancel") toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const fld = (k, l, ph, extra = {}) => (
    <div><span className={label}>{l}</span><input className={input} value={f[k] || ""} placeholder={ph} onChange={e => setF({ ...f, [k]: e.target.value })} {...extra} /></div>
  );
  return (
    <div className="p-4 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-3" data-testid="customer-form">
      {!initial?.id && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="customer-kind">
          {[[false, "Uden pladser", "Fx et byggefirma - I kører fra jeres egne lokationer og kundens adresser."],
            [true, "Med genbrugspladser og containere", "Fx ARGO, der bruger Pickup - pladser, materialer, containere og leveringsadresser som en kunde i ContainerFlow."]].map(([v, t, d]) => (
            <button key={t} type="button" onClick={() => setWithSites(v)}
              className={`text-left rounded-xl border px-3 py-2 ${withSites === v ? "border-emerald-500 bg-white dark:bg-slate-800 ring-2 ring-emerald-500/30" : "border-slate-200 dark:border-slate-700"}`}>
              <span className="block text-sm font-semibold">{t}</span><span className="block text-[11px] text-slate-500">{d}</span>
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {fld("name", "Navn", withSites ? "Fx ARGO" : "Fx Carlsberg Byen A/S", { autoFocus: true })}
        {!withSites && <>
          {fld("cvr", "CVR (valgfri)", "12345678", { inputMode: "numeric" })}
          {fld("address", "Adresse (valgfri)", "Vej, postnr. by")}
          {fld("contact", "Kontaktperson", "Valgfri")}
          {fld("phone", "Telefon", "Valgfri", { inputMode: "tel" })}
          {fld("email", "E-mail", "Valgfri", { type: "email" })}
        </>}
      </div>
      {withSites && <p className="text-[11px] text-slate-500">ContainerFlow opretter kunden og pladserne (så hver kun findes én gang - også når flere vognmænd kører for den) og sætter jer på som vognmand. Skriv gerne pladserne i noten. Derefter tilføjer I selv materialer, containere og leveringsadresser under Lokationer › Egne kunder med pladser.</p>}
      {withSites && <div><span className={label}>Pladser og andet (valgfri)</span><input className={input} value={f.note || ""} onChange={e => setF({ ...f, note: e.target.value })} placeholder="Fx Køge, Hårlev og Bjæverskov Genbrugsplads" data-testid="customer-request-note" /></div>}
      {withSites && <OwnershipNote />}
      {!withSites && <div><span className={label}>Note (valgfri)</span><input className={input} value={f.note || ""} onChange={e => setF({ ...f, note: e.target.value })} placeholder="Fx faktureres månedligt" /></div>}
      {!withSites && <CustomerAddresses value={f.addresses || []} onChange={(addresses) => setF({ ...f, addresses })} />}
      {/* 2026-10-27: Hvor affaldet køres hen, sættes pr. materiale i lokationens kalender (Lokationer › Leveringsadresser og åbningstider). */}
      {initial?.id && f.name.trim() !== initial.name && <p className="text-xs text-amber-700">Navnet ændres også på kundens ture og prisregler.</p>}
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={onCancel}>Annuller</button>
        <button className={btnPrimary} disabled={busy} onClick={save}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {withSites && !initial?.id ? "Send anmodning" : "Gem"}</button>
      </div>
    </div>
  );
}

// 2026-10-28: Her er priserne pr. kunde. Kunderne selv (navn, CVR, adresser, Excel, skjul) oprettes og rettes
// under Kunder - ét sted for alle kunder og deres steder (før: egne kunder her, kunder med pladser under Lokationer).
function CustomersView({ options, reloadOptions, canEdit, goRules, onGoCustomers }) {
  const [rules, setRules] = useState(null);
  const [registered, setRegistered] = useState([]);
  const [newRuleFor, setNewRuleFor] = useState(null);
  const [moving, setMoving] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([axios.get(`${API}/pricing/rules`), axios.get(`${API}/logistics/customers`)]);
      setRules(r.data); setRegistered(c.data || []);
    } catch (e) { toast.error(formatApiError(e)); setRules([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const reloadAll = () => { load(); reloadOptions(); };

  if (!rules) return <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const countFor = (id) => rules.filter(r => (r.conditions?.customer_ids || []).includes(id));
  const general = rules.filter(r => !(r.conditions?.customer_ids || []).length);
  const regById = Object.fromEntries(registered.map(c => [c.id, c]));
  const recycling = options.customers.filter(c => !c.own);
  const own = options.customers.filter(c => c.own && c.id.startsWith("firma:"));
  const noCustomer = options.customers.find(c => c.own && !c.id.startsWith("firma:"));

  const Row = ({ c, kind }) => {
    const list = countFor(c.id);
    const base = list.filter(r => r.kind === "base").length;
    const sur = list.length - base;
    const reg = c.registered_id ? regById[c.registered_id] : null;
    return (
      <li className={`px-4 py-3 ${c.inactive ? "opacity-60" : ""}`} data-testid={`customer-row-${c.name}`}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 dark:text-white">{c.name}</span>
              {kind === "own" && !reg && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500" title="Fundet på egne ture - ikke oprettet i kunderegistret">Fra ture</span>}
              {c.external && <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200" title="Bruger ikke selv ContainerFlow - I vedligeholder pladserne (Lokationer)">Egen kunde med pladser</span>}
              {c.inactive && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Skjult</span>}
            </div>
            <div className="text-xs mt-0.5">
              {list.length === 0
                ? <span className="text-slate-500">Følger de generelle priser ({general.length} regler)</span>
                : <span className="text-emerald-700 dark:text-emerald-300 font-semibold">Egen prisaftale: {base} grundpris{base === 1 ? "" : "er"}{sur ? `, ${sur} tillæg` : ""} - resten følger de generelle</span>}
              {reg && (reg.cvr || reg.contact || reg.phone) && <span className="text-slate-400"> · {[reg.cvr && `CVR ${reg.cvr}`, reg.contact, reg.phone].filter(Boolean).join(" · ")}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {list.length > 0 && <button className={btnGhost} onClick={() => goRules(c.id)}><Tag className="w-4 h-4" /> Se priser</button>}
            {canEdit && (() => {
              // Har kunden allerede en prisaftale, rettes den - ellers oprettes en ny.
              const bid = (list.find(r => r.bundle_id && (r.conditions?.customer_ids || []).length === 1) || {}).bundle_id;
              const existing = bid ? rules.filter(r => r.bundle_id === bid) : [];
              return (
                <button className={btnPrimary} data-testid={`customer-price-${c.name}`}
                  onClick={() => setNewRuleFor(existing.length ? bundleFromRules(existing) : newBundle({ customer_ids: [c.id] }, `${c.name} - prisaftale`))}>
                  {existing.length ? <><Edit2 className="w-4 h-4" /> Ret prisaftale</> : <><Plus className="w-4 h-4" /> Sæt egen pris</>}
                </button>
              );
            })()}
            {reg && reg.active !== false && canEdit && recycling.length > 0 && (
              <button className={btnGhost} title="Kunden er nu en kunde med pladser (i ContainerFlow eller jeres egen) - flyt prisaftalerne og turene" onClick={() => setMoving(reg)} data-testid={`customer-move-${c.name}`}>Flyt til kunde med pladser</button>
            )}
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-4" data-testid="customers-view">
      <div className="flex gap-2 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-sm text-sky-900 dark:text-sky-100">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <b>Sådan sætter du priser pr. kunde:</b> De <b>generelle regler</b> gælder alle kunder. Tryk <b>"Sæt egen pris"</b> ud for en kunde for at lave en regel, der kun gælder den kunde -
          den vinder altid over den generelle. Du kan lave én enkelt afvigelse (fx kun timeprisen) eller en hel prismodel for kunden (grundpris + tillæg).
          Alle betingelser (lokation, materiale, opgavetype, dag, tid, hænger …) kan kombineres med kunden.
        </div>
      </div>

      <section className={`${card} overflow-hidden`}>
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between gap-2">
          <h3 className="font-bold flex items-center gap-2"><Tag className="w-4 h-4 text-slate-400" /> Generelle priser</h3>
          <button className={btnGhost} onClick={() => goRules("general")}>Se {general.length} regler</button>
        </div>
        <p className="px-4 py-3 text-sm text-slate-500">Gælder alle kunder, der ikke har en egen regel for den samme slags tur.</p>
      </section>

      {recycling.length > 0 && (
        <section className={`${card} overflow-hidden`}>
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <h3 className="font-bold flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /> Kunder med pladser</h3>
            <p className="text-xs text-slate-500">Kunder i ContainerFlow, der bruger jer som vognmand - og jeres egne kunder med pladser (fx ARGO). Ture kommer fra bestillinger på deres pladser.</p>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">{recycling.map(c => <Row key={c.id} c={c} kind="recycling" />)}</ul>
        </section>
      )}

      {/* 2026-10-18: Egne kunder er premium - en partner har kun sine genbrugsplads-kunder. */}
      {options.profile?.tier !== "partner" && <section className={`${card} overflow-hidden`}>
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-bold flex items-center gap-2"><Users className="w-4 h-4 text-slate-400" /> Egne kunder</h3>
            <p className="text-xs text-slate-500">Jeres egne slutkunder på egne ture (fx byggefirmaer). Kunderne og deres adresser oprettes og rettes under Kunder - her sætter I deres priser (også før den første tur).</p>
          </div>
          {onGoCustomers && <button className={btnGhost} onClick={onGoCustomers} data-testid="customers-go"><Users className="w-4 h-4" /> Opret og ret kunder</button>}
        </div>
        {own.length === 0 && <p className="px-4 py-6 text-sm text-slate-500 text-center">Ingen egne kunder endnu.</p>}
        <ul className="divide-y divide-slate-100 dark:divide-slate-700">
          {own.map(c => <Row key={c.id} c={c} kind="own" />)}
          {noCustomer && <Row c={{ ...noCustomer, name: "Egne ture uden kunde" }} kind="none" />}
        </ul>
      </section>}

      {moving && <MoveToCompany reg={moving} customers={recycling} onClose={() => setMoving(null)} onDone={() => { setMoving(null); reloadAll(); }} />}
      {newRuleFor && (
        <BundleEditor
          bundle={newRuleFor}
          options={options} firstRule={rules.length === 0}
          onClose={() => setNewRuleFor(null)} onSaved={() => { setNewRuleFor(null); load(); }} />
      )}
    </div>
  );
}

/* ======================================================================
   Prisaftale-editor (2026-10-17)
   Én prisaftale = én grundpris + flere tillæg med SAMME betingelser.
   Før kunne en regel kun være enten grundpris eller tillæg, og skiftede man
   type, forsvandt det man havde tastet i den anden. Tillæg får automatisk
   deres scenarie-betingelse (weekend = lør/søn osv.), så et "weekendtillæg"
   aldrig ved en fejl gælder alle dage.
   ====================================================================== */
const CAT_AUTO_TEXT = {
  weekend: "Lørdag og søndag", holiday: "Helligdage", haster: "Ture med prioritet Haster",
  trailer: "Ture med hænger", evening: "Ture der starter i tidsrummet", waiting: "Kun når der er registreret ventetid", other: "Alle ture i aftalen",
  stop: "Pr. stop ud over de inkluderede",
};
const CAT_NAME = { weekend: "Weekendtillæg", holiday: "Helligdagstillæg", haster: "Hastetillæg", trailer: "Hængertillæg", evening: "Aften/nat-tillæg", waiting: "Ventetid", stop: "Stoptillæg", other: "Andet tillæg" };
const UNIT_SUGGESTIONS = ["paller", "kolli", "m³", "ton", "stk", "kg"];
const AUTO_KEYS = { weekend: ["day_types"], holiday: ["day_types"], haster: ["priorities"], trailer: ["trailer", "trailer_counts"], evening: ["time_from", "time_to"] };
const parseNum = (v) => {
  if (v === "" || v == null) return NaN;
  let s = String(v).replace(/\s|kr|%/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
  return Number(s);
};
const BLANK_PRICING = { model: "fixed", amount: "", unit: "paller", hourly_rate: "", start_fee: "", min_minutes: "", min_amount: "", rounding_mode: "up", rounding_minutes: 15, deduct_pause: true };
// 2026-10-18: Forgæves kørsel er en ekstra grundpris i aftalen (udfald = forgæves).
const isFailedRule = (r) => r.kind === "base" && (r.conditions || {}).outcome === "not_completed";
// 2026-10-20: Hængerkørsel - egen pris for containeren på bilen/hængeren (en
// ekstra grundpris i aftalen med betingelsen Kørsel).
const isRunPart = (r) => r.kind === "base" && !isFailedRule(r) && ((r.conditions || {}).run_roles || []).length === 1;
const runSpecOf = (r) => (r ? { id: r.id, mode: r.run_spec?.mode || "amount",
  value: String(r.run_spec?.value ?? (r.pricing || {}).amount ?? "").replace(".", ",") } : { mode: "same", value: "" });

/** Lav en prisaftale ud fra én eller flere eksisterende regler. */
function bundleFromRules(rules) {
  const base = rules.find(r => r.kind === "base" && !isFailedRule(r) && !isRunPart(r));
  const failed = rules.find(isFailedRule);
  const first = base || rules.find(r => !isFailedRule(r) && !isRunPart(r)) || rules[0] || {};
  const cond = { ...EMPTY_COND, ...(first.conditions || {}) };
  if (!base && first.kind === "surcharge") (AUTO_KEYS[first.category] || []).forEach(k => { cond[k] = EMPTY_COND[k]; });
  return {
    bundle_id: first.bundle_id || "",
    adopt_ids: first.bundle_id ? [] : rules.map(r => r.id),
    name: first.bundle_name || (base ? base.name : first.name) || "",
    code: first.bundle_code || "",
    conditions: cond, valid_from: first.valid_from || "", valid_to: first.valid_to || "",
    active: first.active !== false, note: first.note || "",
    base: base ? { id: base.id, pricing: { ...BLANK_PRICING, ...base.pricing } } : null,
    failed: failed ? { id: failed.id, amount: String((failed.pricing || {}).amount ?? "") } : null,
    run_truck: runSpecOf(rules.find(r => isRunPart(r) && r.conditions.run_roles[0] === "truck")),
    run_trailer: runSpecOf(rules.find(r => isRunPart(r) && r.conditions.run_roles[0] === "trailer")),
    surcharges: rules.filter(r => r.kind === "surcharge").map(r => ({
      id: r.id, category: r.category, unit: (r.surcharge || {}).unit || "kr", value: String((r.surcharge || {}).value ?? ""),
      name: r.name && !r.name.startsWith(`${first.bundle_name || ""} - `) ? r.name : "",
      time_from: (r.conditions || {}).time_from || "", time_to: (r.conditions || {}).time_to || "",
      trailer_counts: (r.conditions || {}).trailer_counts || [],
      included: (r.surcharge || {}).included ?? 2,
      exempt: Number((r.surcharge || {}).value) === 0,
    })),
  };
}
const newBundle = (conditions = {}, name = "") => ({
  bundle_id: "", adopt_ids: [], name, code: "", conditions: { ...EMPTY_COND, ...conditions }, valid_from: "", valid_to: "",
  active: true, note: "", base: { pricing: { ...BLANK_PRICING } }, failed: null, surcharges: [],
  run_truck: { mode: "same", value: "" }, run_trailer: { mode: "same", value: "" },
});

const copyBundle = (b) => ({
  ...b, bundle_id: "", adopt_ids: [], name: `${b.name} (kopi)`, code: "",
  base: b.base ? { pricing: { ...b.base.pricing } } : null,
  failed: b.failed ? { amount: b.failed.amount } : null,
  run_truck: { mode: b.run_truck?.mode || "same", value: b.run_truck?.value || "" },
  run_trailer: { mode: b.run_trailer?.mode || "same", value: b.run_trailer?.value || "" },
  surcharges: b.surcharges.map(({ id, ...rest }) => rest),
});

function bundlePayload(b, effective) {
  return {
    bundle_id: b.bundle_id || undefined, adopt_ids: b.adopt_ids, name: b.name, code: (b.code || "").trim(), conditions: b.conditions,
    valid_from: b.valid_from, valid_to: b.valid_to, active: b.active, note: b.note, effective,
    base: b.base ? { id: b.base.id, pricing: b.base.pricing } : null,
    failed: b.failed ? { id: b.failed.id, amount: b.failed.amount } : null,
    run_truck: b.run_truck && b.run_truck.mode !== "same" ? b.run_truck : null,
    run_trailer: b.run_trailer && b.run_trailer.mode !== "same" ? b.run_trailer : null,
    surcharges: b.surcharges.map(x => ({
      id: x.id, category: x.category, unit: x.exempt ? "kr" : (x.category === "stop" ? "kr" : x.unit), value: x.exempt ? 0 : x.value,
      name: x.exempt ? `Fritaget for ${(CAT_NAME[x.category] || "tillæg").toLowerCase()}` : x.name,
      time_from: x.time_from, time_to: x.time_to, trailer_counts: x.trailer_counts,
      included: x.category === "stop" ? x.included : undefined,
    })),
  };
}

/** Viser hvordan et indtastet beløb forstås (fx "1.000" = 1.000,00 kr). */
function ReadBack({ v, unit }) {
  if (v === "" || v == null) return null;
  const n = parseNum(v);
  if (!Number.isFinite(n)) return <p className="text-[11px] text-red-600 mt-0.5">Ugyldigt tal</p>;
  return <p className="text-[11px] text-emerald-700 mt-0.5">= {n.toLocaleString("da-DK", { minimumFractionDigits: unit === "%" ? 0 : 2, maximumFractionDigits: 2 })} {unit}</p>;
}

/** "Prøv prisen": beregn et tænkt scenarie med aftalen som den står i editoren. */
function PriceTester({ bundle, options }) {
  const [sc, setSc] = useState({ day_type: "weekday", priority: "normal", trailer_count: 0, start_time: "08:00", minutes: 60, waiting_min: 0, containers: 1, qty: 10, stops: 2, outcome: "completed", run_role: "solo" });
  const perUnit = bundle.base?.pricing?.model === "per_unit";
  const unitName = bundle.base?.pricing?.unit || "paller";
  const hasStop = (bundle.surcharges || []).some(x => x.category === "stop");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const c = bundle.conditions || {};
      const scenario = {
        ...sc, customer_id: (c.customer_ids || [])[0] || "", location_code: (c.location_codes || [])[0] || "",
        material: (c.materials || [])[0] || "", materials: c.materials || [], task_type: (c.task_types || [])[0] || "normal",
        units: perUnit ? { [unitName]: parseNum(sc.qty) || 0 } : {},
      };
      const { data } = await axios.post(`${API}/pricing/simulate`, { scenario, draft: bundlePayload(bundle, "now") });
      setRes(data);
    } catch (e) { setRes({ error: formatApiError(e) }); }
    setBusy(false);
  };
  const set = (k, v) => setSc(x => ({ ...x, [k]: v }));
  return (
    <div className="rounded-xl border border-sky-200 dark:border-sky-900 bg-sky-50/60 dark:bg-sky-950/20 p-4 space-y-3" data-testid="price-tester">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-bold">Prøv prisen</h4>
        <span className="text-[11px] text-slate-500">Bruger aftalen som den står her + jeres øvrige regler</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div><span className={label}>Dag</span>
          <select className={input} value={sc.day_type} onChange={e => set("day_type", e.target.value)}>{Object.entries(options.day_types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div><span className={label}>Prioritet</span>
          <select className={input} value={sc.priority} onChange={e => set("priority", e.target.value)}>{Object.entries(options.priorities).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div><span className={label}>Kørsel</span>
          <select className={input} value={sc.run_role} onChange={e => set("run_role", e.target.value)} data-testid="price-tester-run-role">{Object.entries(RUN_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div><span className={label}>Hængere (varekørsel)</span>
          <select className={input} value={sc.trailer_count} onChange={e => set("trailer_count", Number(e.target.value))}>{[0, 1, 2, 3].map(n => <option key={n} value={n}>{n === 0 ? "Ingen" : n}</option>)}</select></div>
        <div><span className={label}>Start</span><input type="time" className={input} value={sc.start_time} onChange={e => set("start_time", e.target.value)} /></div>
        <div><span className={label}>Varighed (min)</span><input inputMode="numeric" className={input} value={sc.minutes} onChange={e => set("minutes", e.target.value)} /></div>
        <div><span className={label}>Ventetid (min)</span><input inputMode="numeric" className={input} value={sc.waiting_min} onChange={e => set("waiting_min", e.target.value)} /></div>
        {perUnit ? <div><span className={label}>Antal {unitName}</span><input inputMode="decimal" className={input} value={sc.qty} onChange={e => set("qty", e.target.value)} /></div>
          : <div><span className={label}>Containere</span><input inputMode="numeric" className={input} value={sc.containers} onChange={e => set("containers", e.target.value)} /></div>}
        {hasStop && <div><span className={label}>Antal stop</span><input inputMode="numeric" className={input} value={sc.stops} onChange={e => set("stops", e.target.value)} /></div>}
        {bundle.failed && <div><span className={label}>Udfald</span>
          <select className={input} value={sc.outcome} onChange={e => set("outcome", e.target.value)}><option value="completed">Gennemført</option><option value="not_completed">Forgæves</option></select></div>}
        <div className="flex items-end"><button type="button" className={`${btnPrimary} w-full justify-center`} onClick={run} disabled={busy} data-testid="price-tester-run">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Beregn"}</button></div>
      </div>
      {res?.error && <p className="text-sm text-red-600">{res.error}</p>}
      {res && !res.error && (
        <div className="rounded-lg bg-white dark:bg-slate-900 p-3 text-sm space-y-1">
          {(res.lines || []).map((l, i) => (
            <div key={i} className="flex justify-between gap-3"><span className={l.from_draft ? "" : "text-slate-500"}>{l.label}{!l.from_draft && l.rule_name ? " (anden regel)" : ""}</span><span className="font-mono">{fmtKr(l.amount)}</span></div>
          ))}
          {res.status !== "ok" && <p className="text-red-600">{(res.flags || []).join(" · ") || "Ingen pris"}</p>}
          <div className="flex justify-between border-t border-slate-100 dark:border-slate-700 pt-1 font-bold"><span>I alt (ekskl. moms)</span><span className="font-mono">{fmtKr(res.total)}</span></div>
          {(res.matching_base || []).length > 1 && <p className="text-[11px] text-amber-700">{res.matching_base.length} grundpriser passer på dette scenarie. Den der bruges: {res.matching_base[0]} (den mest specifikke, ved lighed den nyeste).</p>}
        </div>
      )}
    </div>
  );
}

/* 2026-10-23: Regeltjek - finder ud fra de rigtige ture de steder, hvor
   "den mest præcise aftale vinder" ikke giver det, man forventer. */
const SEV = {
  high: { label: "Ret", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200" },
  medium: { label: "Tjek", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
  info: { label: "Info", cls: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" },
  low: { label: "Ubrugt", cls: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300" },
};
function RuleCheck({ reloadKey, onOpen, canEdit }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/pricing/rules/check`).then(r => { if (alive) setData(r.data); }).catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [reloadKey]);
  if (!data) return null;
  const f = data.findings || [];
  const important = f.filter(x => x.severity === "high" || x.severity === "medium");
  return (
    <div className={`${card} overflow-hidden`} data-testid="rule-check">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
        <ShieldCheck className={`w-5 h-5 ${important.length ? "text-amber-600" : "text-emerald-600"}`} />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Regeltjek <span className="font-normal text-slate-500">· de sidste {data.days} dage ({data.tours} ture)</span></div>
          <div className="text-xs text-slate-600 dark:text-slate-300" data-testid="rule-check-summary">
            {important.length ? `${important.length} ${important.length === 1 ? "sted" : "steder"}, hvor aftalerne konkurrerer - tjek dem` : "Ingen aftaler konkurrerer om de samme ture"}
            {f.length > important.length ? ` · ${f.length - important.length} til orientering` : ""}
          </div>
        </div>
        {f.length > 0 && <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />}
      </button>
      {open && f.length > 0 && (
        <div className="border-t border-slate-100 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
          {f.map((x, i) => (
            <div key={i} className="px-4 py-3 space-y-1" data-testid={`rule-check-${x.kind}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SEV[x.severity]?.cls || ""}`}>{SEV[x.severity]?.label}</span>
                <span className="font-semibold text-sm">{x.title}</span>
                {x.count > 0 && <span className="text-xs text-slate-500">{x.count} ture</span>}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300">{x.text}</div>
              {(x.examples || []).map((e, j) => (
                <div key={j} className="text-[11px] text-slate-500">
                  Fx {fmtDate(e.date)} · {e.customer}{e.location ? ` · ${e.location}` : ""}{e.material ? ` · ${e.material}` : ""}
                  {e.price != null && <> · nu <b>{fmtKr(e.price)}</b>{e.other_price != null && e.other_price !== e.price ? <> · «{x.loser}» ville give <b>{fmtKr(e.other_price)}</b></> : null}</>}
                </div>
              ))}
              {canEdit && (x.winner_group || x.loser_group) && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {x.winner && <button className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:underline" onClick={() => onOpen(x, "winner")}>Åbn «{x.winner}»</button>}
                  {x.loser && <button className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:underline" onClick={() => onOpen(x, "loser")}>Åbn «{x.loser}»</button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* 2026-10-23: Prisregulering - hæv/sænk flere aftaler med en procent fra en
   kørselsdato. Den gamle aftale gælder ture kørt til dagen før, en ny med de
   nye beløb fra datoen (samme som "Ture kørt fra en dato" på én aftale). */
function RegulateModal({ rules, options, onClose, onDone }) {
  const nextJan = `${new Date().getFullYear() + 1}-01-01`;
  const [pct, setPct] = useState("");
  const [day, setDay] = useState(nextJan);
  const [roundTo, setRoundTo] = useState(1);
  const bundles = useMemo(() => Object.values(rules.filter(r => r.bundle_id).reduce((m, r) => {
    (m[r.bundle_id] = m[r.bundle_id] || []).push(r); return m;
  }, {})).sort((a, b) => (a[0].bundle_name || "").localeCompare(b[0].bundle_name || "", "da")), [rules]);
  const eligible = (rs) => !rs.some(r => (r.valid_to && r.valid_to < day) || (r.valid_from && r.valid_from >= day));
  const [sel, setSel] = useState(() => new Set(bundles.filter(rs => !rs.some(r => r.valid_to)).map(rs => rs[0].bundle_id)));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = { bundle_ids: [...sel], percent: pct, date: day, round_to: roundTo };
  const run = async (apply) => {
    if (!parseNum(pct)) { toast.error("Skriv en procent, fx 4,5"); return; }
    if (!sel.size) { toast.error("Vælg mindst én prisaftale"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pricing/regulate`, { ...body, apply });
      if (apply) { toast.success(`${data.regulated} prisaftaler er reguleret fra ${fmtDate(day)}`); onDone(); }
      else setPreview(data.bundles);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const num = (v) => Number(v).toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-2xl max-h-[100dvh] sm:max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-800 sm:rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-testid="regulate-modal">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">Prisregulering</h3>
            <p className="text-xs text-slate-500">Fx inflation: alle beløb i de valgte aftaler ændres med en procent for ture kørt fra datoen. Procent-tillæg ændres ikke.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><span className={label}>Ændring i %</span>
              <input className={input} inputMode="decimal" value={pct} onChange={e => { setPct(e.target.value); setPreview(null); }} placeholder="Fx 4,5" data-testid="regulate-pct" /></div>
            <div><span className={label}>For ture kørt fra</span>
              <input type="date" className={input} value={day} onChange={e => { setDay(e.target.value); setPreview(null); }} data-testid="regulate-date" /></div>
            <div><span className={label}>Afrund til</span>
              <div className="flex flex-wrap gap-1">
                {[[0, "Øre"], [1, "Hele kr"], [5, "5 kr"], [10, "10 kr"]].map(([v, l]) => (
                  <button key={v} type="button" className={`${chipBase} ${roundTo === v ? chipOn : chipOff}`} onClick={() => { setRoundTo(v); setPreview(null); }}>{l}</button>
                ))}
              </div></div>
          </div>
          {day < localDate() && <div className="text-xs text-amber-700">Datoen ligger tilbage i tiden: ture kørt siden da og ikke faktureret får den nye pris.</div>}
          <div>
            <div className="flex items-center justify-between">
              <span className={label}>Prisaftaler ({sel.size} valgt)</span>
              <div className="flex gap-2 text-xs">
                <button className="text-emerald-700 font-semibold" onClick={() => { setSel(new Set(bundles.filter(eligible).map(rs => rs[0].bundle_id))); setPreview(null); }}>Vælg alle</button>
                <button className="text-slate-500 font-semibold" onClick={() => { setSel(new Set()); setPreview(null); }}>Fravælg alle</button>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700 max-h-64 overflow-y-auto">
              {bundles.map(rs => {
                const bb = bundleFromRules(rs);
                const ok = eligible(rs);
                const on = sel.has(bb.bundle_id);
                return (
                  <label key={bb.bundle_id} className={`flex items-start gap-3 px-3 py-2 text-sm ${ok ? "cursor-pointer" : "opacity-50"}`}>
                    <input type="checkbox" className="mt-1 accent-emerald-600" disabled={!ok} checked={on && ok}
                      onChange={() => { setSel(s => { const n = new Set(s); n.has(bb.bundle_id) ? n.delete(bb.bundle_id) : n.add(bb.bundle_id); return n; }); setPreview(null); }} />
                    <span className="min-w-0">
                      <span className="font-semibold">{bb.name}</span>
                      <span className="block text-[11px] text-slate-500">{conditionChips(bb.conditions, options).join(" · ") || "Alle ture"}
                        {bb.valid_from || bb.valid_to ? ` · kørt ${bb.valid_from ? `fra ${fmtDate(bb.valid_from)}` : ""}${bb.valid_to ? ` til ${fmtDate(bb.valid_to)}` : ""}` : ""}
                        {!ok ? " · gælder ikke på datoen" : ""}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          {preview && (
            <div className="space-y-2" data-testid="regulate-preview">
              <div className={label}>Ændringer</div>
              {preview.map(p => (
                <div key={p.bundle_id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 text-sm">
                  <div className="font-semibold">{p.name}{!p.ok && <span className="ml-2 text-xs text-amber-700">{p.reason} - springes over</span>}</div>
                  {p.ok && p.changes.length === 0 && <div className="text-xs text-slate-500">Ingen beløb at ændre (kun procent-tillæg).</div>}
                  {p.ok && p.changes.map((c, i) => (
                    <div key={i} className="text-xs text-slate-600 dark:text-slate-300 flex justify-between gap-2">
                      <span className="truncate">{(c.rule || "").startsWith(p.name) ? (c.rule.slice(p.name.length).replace(/^ - /, "") || "Grundpris") : c.rule} · {c.field}</span>
                      <span className="tabular-nums whitespace-nowrap">{num(c.old)} → <b>{num(c.new)}</b></span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button className={btnGhost} onClick={onClose}>Annuller</button>
            {!preview
              ? <button className={btnPrimary} disabled={busy} onClick={() => run(false)} data-testid="regulate-preview-btn">Vis ændringer</button>
              : <button className={btnPrimary} disabled={busy} onClick={() => run(true)} data-testid="regulate-apply">Gennemfør prisregulering</button>}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function BundleEditor({ bundle: initial, options, onClose, onSaved, firstRule = false, fromReport = false }) {
  // 2026-10-18: "Opret pris" fra Rapport gælder også ture UDEN pris (ellers
  // fik turen man kom fra stadig ingen pris). Ture med pris ændres ikke.
  // 2026-10-25: Prisen følger KØRSELSDATOEN - én regel for alt: en ny aftale
  // gælder ture kørt fra i dag (også dem, der allerede er oprettet) + ture uden
  // pris; en ændring gælder ture kørt fra en dato (standard i dag). "Kun ture
  // oprettet fra nu" er fjernet: den gav to priser for samme opgave samme dag
  // (ture oprettet før og efter ændringen).
  const isEdit = !!(initial.bundle_id || (initial.adopt_ids || []).length);
  const [effective, setEffective] = useState(isEdit ? "driven" : "unpriced");
  const [effDate, setEffDate] = useState(() => localDate());
  const [b, setB] = useState(() => JSON.parse(JSON.stringify(initial)));
  const [more, setMore] = useState(() => {
    const c = initial.conditions || {};
    return !!(c.delivery_labels?.length || c.priorities?.length || c.trailer || c.trailer_counts?.length || c.container_labels?.length || c.time_from || (c.outcome && c.outcome !== "completed") || initial.valid_from || initial.valid_to);
  });
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const editing = !!(initial.bundle_id || (initial.adopt_ids || []).length);
  const c = b.conditions;
  const setC = (k, v) => setB(x => ({ ...x, conditions: { ...x.conditions, [k]: v } }));
  // 2026-10-23: En lokation tilhører én kunde. Uden kunden på tabte aftalen
  // til kundens almindelige aftale (kunde vægter mest) - så kunden sættes på.
  const pickLocations = (v) => setB(x => {
    const cond = { ...x.conditions, location_codes: v };
    if (v.length && !(x.conditions.customer_ids || []).length) {
      const ownIds = new Set(options.customers.filter(cu => cu.own).map(cu => cu.id));
      const owners = [...new Set(v.map(code => options.locations.find(l => l.code === code)?.customer_id).filter(Boolean))];
      if (owners.length && owners.every(o => !ownIds.has(o))) cond.customer_ids = owners;
    }
    return { ...x, conditions: cond };
  });
  const setP = (k, v) => setB(x => ({ ...x, base: { ...x.base, pricing: { ...x.base.pricing, [k]: v } } }));
  const setS = (i, patch) => setB(x => ({ ...x, surcharges: x.surcharges.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const locs = options.locations.filter(l => !c.customer_ids.length || c.customer_ids.includes(l.customer_id));
  // 2026-10-24 (F-9): Leveringsadresse er navnet fra en kundes leveringskalender
  // (bestillinger på kundens pladser). Egne ture har ingen - en aftale kun for
  // egne kunder med en leveringsadresse ville aldrig ramme en tur.
  const ownIdSet = new Set(options.customers.filter(cu => cu.own).map(cu => cu.id));
  const ownOnly = c.customer_ids.length > 0 && c.customer_ids.every(id => ownIdSet.has(id));
  const p = b.base?.pricing || BLANK_PRICING;
  const customRounding = ![15, 30, 60].includes(Number(p.rounding_minutes));
  const usedCats = new Set(b.surcharges.map(x => x.category));

  // Fejl vises ved feltet før der gemmes - intet forsvinder i stilhed.
  const problems = [];
  if (!b.name.trim()) problems.push("Giv aftalen et navn");
  if (!b.base && !b.surcharges.length) problems.push("Tilføj en grundpris og/eller mindst ét tillæg");
  if (b.base) {
    if (p.model === "hourly" && !(parseNum(p.hourly_rate) >= 0)) problems.push("Timepris mangler");
    if (p.model !== "hourly" && !(parseNum(p.amount) >= 0)) problems.push("Grundprisens beløb mangler");
    if (p.model === "per_unit" && !(p.unit || "").trim()) problems.push("Vælg enhed (fx paller)");
  }
  if (b.failed && !(parseNum(b.failed.amount) >= 0)) problems.push("Beløb for forgæves kørsel mangler");
  [["run_truck", "containeren på bilen"], ["run_trailer", "containeren på hængeren"]].forEach(([k, l]) => {
    const x = b[k];
    if (x && x.mode !== "same" && !(parseNum(x.value) >= 0)) problems.push(`Pris for ${l} mangler`);
    if (x && x.mode === "percent" && !b.base) problems.push(`Procent for ${l} kræver en grundpris`);
  });
  if (effective === "driven" && !effDate) problems.push("Vælg dato for kørsel");
  b.surcharges.forEach((x, i) => { if (!x.exempt && !(parseNum(x.value) >= 0)) problems.push(`Tillæg ${i + 1} (${options.categories[x.category]}) mangler beløb`); });

  const save = async (force = false) => {
    if (problems.length) { toast.error(problems[0]); return; }
    if (!force && !c.customer_ids.length && !c.location_codes.length && b.base && !firstRule
        && !window.confirm("Aftalen har hverken kunde eller lokation, så dens grundpris gælder alle kunder, der ikke har deres egen. Er det meningen?")) return;
    setSaving(true);
    try {
      const body = effective === "driven"
        ? { ...bundlePayload(b, "all"), driven_from: effDate, ...(editing ? {} : { valid_from: effDate }) }
        : bundlePayload(b, effective === "date" ? effDate : effective);
      await axios.post(`${API}/pricing/bundles`, { ...body, force });
      toast.success("Prisaftalen er gemt");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.conflicts) setConflicts(d.conflicts);
      else toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-3xl max-h-[100dvh] sm:max-h-[94vh] overflow-y-auto bg-white dark:bg-slate-800 sm:rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-testid="rule-editor">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">{editing ? "Ret prisaftale" : "Ny prisaftale"}</h3>
            <p className="text-xs text-slate-500">Grundpris og tillæg for de samme ture - alt på ét sted.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
            <div><span className={label}>Navn på aftalen</span><input className={input} value={b.name} onChange={e => setB({ ...b, name: e.target.value })} placeholder="Fx ARGO Roskilde 2026" data-testid="bundle-name" /></div>
            {/* 2026-10-26: Aftale-ID - nøglen i Excel-skabelonen (sammen med Gælder fra), samme gennem alle versioner. */}
            <div><span className={label}>Aftale-ID <InfoTip text="Bruges i Excel-skabelonen (Hent nuværende data / Importér): Aftale-ID + Gælder fra er nøglen. Følger aftalen gennem alle versioner. Tom = laves automatisk (PA-0001 ...)." /></span>
              <input className={input} value={b.code || ""} onChange={e => setB({ ...b, code: e.target.value })} placeholder="Tom = laves selv" maxLength={40} data-testid="bundle-code" /></div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-bold">Gælder for <span className="font-normal text-slate-500">(tomt = alle)</span></h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MultiPick title="Kunder" items={options.customers.map(x => ({ value: x.id, label: x.own ? `${x.name} · egen kunde` : x.name }))} value={c.customer_ids} onChange={v => setC("customer_ids", v)} allLabel="Alle kunder" />
              <MultiPick title="Lokationer" items={locs.map(x => ({ value: x.code, label: x.name }))} value={c.location_codes} onChange={pickLocations} allLabel="Alle lokationer" />
              <MultiPick title="Opgavetype" items={Object.entries(options.task_types).map(([k, v]) => ({ value: k, label: v }))} value={c.task_types} onChange={v => setC("task_types", v)} />
              <MultiPick title="Materiale" items={options.materials.map(m => ({ value: m, label: m }))} value={c.materials} onChange={v => setC("materials", v)} allLabel="Alle materialer" />
            </div>
            <button type="button" className="text-sm font-semibold text-emerald-700 dark:text-emerald-300" onClick={() => setMore(m => !m)}>{more ? "− Færre betingelser" : "+ Flere betingelser (dag, leveringsadresse, container, hængere, udfald, periode ...)"}</button>
            {more && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MultiPick title="Dagtype" items={Object.entries(options.day_types).map(([k, v]) => ({ value: k, label: v }))} value={c.day_types} onChange={v => setC("day_types", v)} allLabel="Alle dage" />
                {/* 2026-10-27: Leveringsadresse = kundens anlæg på bestillinger, og jeres modtageanlæg på egne ture. */}
                <div>
                  <MultiPick title="Leveringsadresse" items={options.delivery_labels.map(m => ({ value: m, label: m, hint: options.delivery_label_info?.[m] || "" }))} value={c.delivery_labels} onChange={v => setC("delivery_labels", v)} />
                  {(c.delivery_labels || []).length > 0 && ownOnly && (
                    <span className="text-[11px] text-slate-500" data-testid="bundle-delivery-own">Egne ture: kun når afleveringen er valgt fra jeres modtageanlæg (ikke skrevet som tekst).</span>
                  )}
                </div>
                <MultiPick title="Containerstørrelse" items={options.container_labels.map(m => ({ value: m, label: m }))} value={c.container_labels} onChange={v => setC("container_labels", v)} />
                <div><span className={label}>Antal hængere</span>
                  <div className="flex flex-wrap gap-1.5">
                    {[1, 2, 3].map(n => {
                      const on = (c.trailer_counts || []).map(Number).includes(n);
                      return <button key={n} type="button" className={`${chipBase} ${on ? chipOn : chipOff}`}
                        onClick={() => setC("trailer_counts", on ? (c.trailer_counts || []).filter(x => Number(x) !== n) : [...(c.trailer_counts || []), n])}>{n === 3 ? "3+" : n}</button>;
                    })}
                  </div>
                  <span className="block mt-1 text-[11px] leading-snug text-slate-500">Varekørsel: fx én aftale for 1 hænger (700 kr) og én for 2 hængere (1.100 kr). En containertur i en hængerkørsel tæller som 1 - giv den sin egen pris under "Hængerkørsel" nedenfor.</span></div>
                <div><span className={label}>Udfald</span>
                  <Segmented value={c.outcome} onChange={v => setC("outcome", v)} options={[{ value: "completed", label: "Gennemført" }, { value: "not_completed", label: "Forgæves" }, { value: "any", label: "Begge" }]} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className={label}>Kørselsdato fra</span><input type="date" className={input} value={b.valid_from} onChange={e => setB({ ...b, valid_from: e.target.value })} /></div>
                  <div><span className={label}>Kørselsdato til</span><input type="date" className={input} value={b.valid_to} onChange={e => setB({ ...b, valid_to: e.target.value })} /></div>
                  <span className="col-span-2 text-[11px] leading-snug text-slate-500">Til en aftale, der starter eller slutter på en dato (fx en kontrakt til 31.12). Skal prisen ændres fra en dato, så brug "Ture kørt fra en dato" nederst - eller Prisregulering for flere aftaler på én gang.</span>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-bold">Grundpris</h4>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="accent-emerald-600 w-4 h-4" checked={!!b.base} data-testid="bundle-has-base"
                  onChange={e => setB({ ...b, base: e.target.checked ? (b.base || { pricing: { ...BLANK_PRICING } }) : null })} />
                Aftalen har en grundpris
              </label>
            </div>
            {!b.base && <p className="text-xs text-slate-500">Kun tillæg - grundprisen kommer fra en anden regel (fx den generelle).</p>}
            {b.base && (
              <>
                <Segmented value={p.model} onChange={v => setP("model", v)} options={Object.entries(options.models).map(([k, v]) => ({ value: k, label: v }))} />
                {HELP.model[p.model] && <p className="text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 rounded-lg px-3 py-2">{HELP.model[p.model]}</p>}
                {p.model === "per_unit" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="bundle-per-unit">
                    <div><span className={label}>Enhed</span>
                      <SuggestInput className={input} value={p.unit || ""} onChange={v => setP("unit", v)} options={UNIT_SUGGESTIONS} placeholder="paller" />
                      <span className="text-[11px] text-slate-500">Samme enhed som på turens linjer</span></div>
                    <div><span className={label}>Pris pr. {p.unit || "enhed"} (kr)</span>
                      <input inputMode="decimal" className={input} value={p.amount} onChange={e => setP("amount", e.target.value)} placeholder="Fx 150" data-testid="bundle-base-amount" />
                      <ReadBack v={p.amount} unit="kr" /></div>
                    <div><span className={label}>Minimum pr. tur (valgfri)</span>
                      <input inputMode="decimal" className={input} value={p.min_amount || ""} onChange={e => setP("min_amount", e.target.value)} placeholder="Fx 1.000" />
                      <ReadBack v={p.min_amount} unit="kr" /></div>
                  </div>
                ) : p.model !== "hourly" ? (
                  <div className="max-w-xs"><span className={label}>{p.model === "per_container" ? "Pris pr. container (kr)" : "Pris pr. tur (kr)"}</span>
                    <input inputMode="decimal" className={input} value={p.amount} onChange={e => setP("amount", e.target.value)} placeholder="Fx 1.100" data-testid="bundle-base-amount" />
                    <ReadBack v={p.amount} unit="kr" /></div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><span className={label}>Timepris (kr)</span><input inputMode="decimal" className={input} value={p.hourly_rate} onChange={e => setP("hourly_rate", e.target.value)} /></div>
                    <div><span className={label}>Startgebyr</span><input inputMode="decimal" className={input} placeholder="Valgfri" value={p.start_fee || ""} onChange={e => setP("start_fee", e.target.value)} /></div>
                    <div><span className={label}>Min. minutter</span><input inputMode="numeric" className={input} placeholder="Valgfri" value={p.min_minutes || ""} onChange={e => setP("min_minutes", e.target.value)} /></div>
                    <div><span className={label}>Min. beløb</span><input inputMode="decimal" className={input} placeholder="Valgfri" value={p.min_amount || ""} onChange={e => setP("min_amount", e.target.value)} /></div>
                    <div className="col-span-2 md:col-span-4">
                      <span className={label}>Afrunding af tiden</span>
                      <div className="flex flex-wrap items-center gap-2">
                        <Segmented value={p.rounding_mode} onChange={v => setP("rounding_mode", v)} options={[{ value: "none", label: "Ingen" }, { value: "up", label: "Op" }, { value: "nearest", label: "Nærmeste" }, { value: "down", label: "Ned" }]} />
                        {p.rounding_mode !== "none" && (
                          <>
                            {[15, 30, 60].map(m => <button key={m} type="button" className={`${chipBase} ${Number(p.rounding_minutes) === m ? chipOn : chipOff}`} onClick={() => setP("rounding_minutes", m)}>{m} min</button>)}
                            <input inputMode="numeric" className={`${inputFit} w-24 ${customRounding ? "ring-2 ring-emerald-500/40" : ""}`} value={p.rounding_minutes} onChange={e => setP("rounding_minutes", e.target.value)} />
                            <span className="text-xs text-slate-500">min</span>
                          </>
                        )}
                      </div>
                    </div>
                    <label className="col-span-2 md:col-span-4 flex items-center gap-2 text-sm">
                      <input type="checkbox" className="accent-emerald-600 w-4 h-4" checked={!!p.deduct_pause} onChange={e => setP("deduct_pause", e.target.checked)} />
                      Træk pauser fra (pauser chaufføren starter under "Tid" mens turen kører)
                    </label>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2" data-testid="bundle-run">
            <span className="text-sm font-bold">Hængerkørsel <InfoTip text="Når chaufføren kører flere containere på én gang: én på bilen og 1-2 på hængeren. Her kan containeren på hængeren (og evt. den på bilen) have sin egen pris - fx solo 1.000 kr og på hængeren 700 kr. En tur kørt alene får altid grundprisen. Ved timepris deles tiden i forvejen mellem turene, der kører samtidig. En container hentet på en ANDEN plads end bilens får som standard fuld pris (som kørt alene) - se Indstillinger." /></span>
            <p className="text-[12px] text-slate-500">Gælder containerture. Ture kørt alene (solo) får grundprisen.</p>
            {[["run_trailer", "Container på hængeren"], ["run_truck", "Container på bilen"]].map(([k, l]) => {
              const x = b[k] || { mode: "same", value: "" };
              return (
                <div key={k} className="space-y-1.5 pt-1" data-testid={`bundle-${k}`}>
                  <span className="block text-sm font-semibold">{l}</span>
                  <div className="flex flex-wrap items-start gap-2">
                    <Segmented value={x.mode} onChange={v => setB({ ...b, [k]: { ...x, mode: v } })}
                      options={[{ value: "same", label: "Som grundprisen" }, { value: "amount", label: "Fast beløb" }, { value: "percent", label: "% af grundprisen" }]} />
                    {x.mode !== "same" && (
                      <div className="w-36"><input inputMode="decimal" className={input} value={x.value} data-testid={`bundle-${k}-value`}
                        onChange={e => setB({ ...b, [k]: { ...x, value: e.target.value } })} placeholder={x.mode === "percent" ? "Fx 70" : "Fx 700"} />
                        <ReadBack v={x.value} unit={x.mode === "percent" ? "%" : "kr"} /></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-2" data-testid="bundle-failed">
            <label className="flex items-center justify-between gap-2">
              <span className="text-sm font-bold">Forgæves kørsel <InfoTip text="Prisen når chaufføren har markeret turen som 'Kan ikke gennemføres' (fx lukket, ingen adgang, forkert affald). Uden den står forgæves ture som 'Mangler pris'." /></span>
              <span className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="accent-emerald-600 w-4 h-4" checked={!!b.failed} data-testid="bundle-has-failed"
                  onChange={e => setB({ ...b, failed: e.target.checked ? (b.failed || { amount: "" }) : null })} />
                Aftalen har en pris for forgæves kørsel
              </span>
            </label>
            {b.failed && (
              <div className="max-w-xs"><span className={label}>Pris ved forgæves kørsel (kr)</span>
                <input inputMode="decimal" className={input} value={b.failed.amount} onChange={e => setB({ ...b, failed: { ...b.failed, amount: e.target.value } })} placeholder="Fx 450" data-testid="bundle-failed-amount" />
                <ReadBack v={b.failed.amount} unit="kr" /></div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3" data-testid="bundle-surcharges">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-bold">Tillæg <span className="font-normal text-slate-500">({b.surcharges.length})</span></h4>
              <span className="text-[11px] text-slate-500">Lægges oveni grundprisen i de nævnte situationer</span>
            </div>
            {b.surcharges.map((x, i) => (
              <div key={x.id || i} className="rounded-lg bg-slate-50 dark:bg-slate-900 p-3 space-y-2" data-testid={`bundle-surcharge-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <select className={`${inputFit} w-auto`} value={x.category} onChange={e => setS(i, { category: e.target.value })}>
                    {Object.entries(options.categories).map(([k, v]) => <option key={k} value={k} disabled={k !== x.category && usedCats.has(k) && !["evening", "trailer", "other"].includes(k)}>{v}</option>)}
                  </select>
                  <InfoTip text={HELP.category[x.category]} />
                  <span className="text-xs text-slate-500 flex-1 min-w-[140px]">Gælder: {CAT_AUTO_TEXT[x.category]}</span>
                  <button type="button" className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Fjern tillæg" onClick={() => setB({ ...b, surcharges: b.surcharges.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></button>
                </div>
                {x.category === "evening" && (
                  <div className="flex items-center gap-2 max-w-xs">
                    <input type="time" className={input} value={x.time_from || "18:00"} onChange={e => setS(i, { time_from: e.target.value })} />
                    <span>–</span>
                    <input type="time" className={input} value={x.time_to || "06:00"} onChange={e => setS(i, { time_to: e.target.value })} />
                  </div>
                )}
                {x.category === "trailer" && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-slate-500">Kun ved antal hængere:</span>
                    {[1, 2, 3].map(n => {
                      const on = (x.trailer_counts || []).map(Number).includes(n);
                      return <button key={n} type="button" className={`${chipBase} ${on ? chipOn : chipOff}`}
                        onClick={() => setS(i, { trailer_counts: on ? (x.trailer_counts || []).filter(v => Number(v) !== n) : [...(x.trailer_counts || []), n] })}>{n === 3 ? "3+" : n}</button>;
                    })}
                    <span className="text-[11px] text-slate-400">(tomt = med hænger)</span>
                  </div>
                )}
                {x.category === "stop" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-500">Stop med i grundprisen:</span>
                    <input inputMode="numeric" className={`${inputFit} w-20`} value={x.included ?? 2} onChange={e => setS(i, { included: e.target.value.replace(/\D/g, "") })} data-testid={`bundle-stop-included-${i}`} />
                    <span className="text-[11px] text-slate-400">Beløbet nedenfor er pr. ekstra stop</span>
                  </div>
                )}
                <div className="flex flex-wrap items-end gap-2">
                  <Segmented value={x.exempt ? "exempt" : x.unit} onChange={v => setS(i, v === "exempt" ? { exempt: true } : { exempt: false, unit: v })}
                    options={x.category === "stop" ? [{ value: "kr", label: "kr pr. ekstra stop" }, { value: "exempt", label: "Fritaget" }]
                      : [{ value: "kr", label: "kr pr. tur" }, { value: "percent", label: "% af grundpris" }, { value: "per_hour", label: "kr pr. time" }, { value: "exempt", label: "Fritaget" }]} />
                  {!x.exempt && <div className="w-36"><input inputMode="decimal" className={input} value={x.value} onChange={e => setS(i, { value: e.target.value })} placeholder={x.unit === "percent" ? "Fx 25" : "Fx 500"} data-testid={`bundle-surcharge-value-${i}`} />
                    <ReadBack v={x.value} unit={x.unit === "percent" ? "%" : x.unit === "per_hour" ? "kr/t" : "kr"} /></div>}
                  {x.exempt && <span className="text-xs text-slate-500 pb-2">Kunden betaler ikke dette tillæg (overstyrer generelle tillæg).</span>}
                </div>
              </div>
            ))}
            <button type="button" className={btnGhost} data-testid="bundle-add-surcharge"
              onClick={() => {
                const next = ["weekend", "haster", "holiday", "trailer", "evening", "waiting", "stop", "other"].find(k => !usedCats.has(k)) || "other";
                setB({ ...b, surcharges: [...b.surcharges, { category: next, unit: "kr", value: "", time_from: "", time_to: "", trailer_counts: [], included: 2, exempt: false }] });
              }}><Plus className="w-4 h-4" /> Tilføj tillæg</button>
          </div>

          <PriceTester bundle={b} options={options} />

          <div>
            <span className={label}>Jeres forklaring (valgfri - vises ved ⓘ)</span>
            <textarea rows={2} maxLength={1000} className={input} value={b.note} onChange={e => setB({ ...b, note: e.target.value })}
              placeholder="Fx: Rammeaftale 2026 med ARGO. Weekend aftalt pr. mail 3/1." data-testid="rule-note" />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="accent-emerald-600 w-4 h-4" checked={b.active} onChange={e => setB({ ...b, active: e.target.checked })} id="rule-active" />
            <label htmlFor="rule-active">Aktiv</label>
          </div>
          <div className="space-y-2" data-testid="rule-effective">
            <h4 className="text-sm font-bold">Hvilke ture gælder {editing ? "ændringen" : "aftalen"} for?</h4>
            <Segmented value={effective} onChange={setEffective} options={[
              ...(!editing ? [{ value: "unpriced", label: "Fra i dag + ture uden pris" }] : []),
              { value: "driven", label: "Ture kørt fra en dato" },
              { value: "all", label: editing ? "Alle - ret en fejl" : "Alle, også kørte ture" },
            ]} />
            {effective === "driven" && (
              <div className="max-w-xs"><span className={label}>Kørt fra og med</span><input type="date" className={input} value={effDate} onChange={e => setEffDate(e.target.value)} data-testid="rule-driven-from" /></div>
            )}
            <p className="text-xs text-slate-500">
              {effective === "unpriced" && `Gælder ture, der køres fra i dag - også dem, der allerede er oprettet (fx ugens faste tømninger) - og ældre ture, der mangler pris${fromReport ? " (fx den tur du kom fra)" : ""}. Ture, der er kørt med en pris, ændres ikke.`}
              {effective === "driven" && (editing
                ? "Ture, der køres fra datoen, får ændringen - også ture, der allerede er oprettet (fx en serie eller ugens faste tømninger). Ture kørt før datoen beholder den gamle pris. Til mange aftaler på én gang: Prisregulering."
                : "Gælder ture der køres fra datoen - også ture der allerede er oprettet.")}
              {effective === "all" && (editing
                ? "Til at rette en fejl i aftalen: ændrer også prisen på ture, der allerede er kørt, men ikke markeret som faktureret. Fakturerede ture ændres aldrig."
                : "Gælder også ture, der allerede er kørt, men ikke markeret som faktureret. Fakturerede ture ændres aldrig.")}
            </p>
          </div>
          {problems.length > 0 && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-3 py-2 text-sm text-amber-900 dark:text-amber-100" data-testid="bundle-problems">
              {problems.map(x => <div key={x}>• {x}</div>)}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-5 py-3 flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose}>Annuller</button>
          <button className={btnPrimary} disabled={saving} onClick={() => save(false)} data-testid="bundle-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem prisaftale</button>
        </div>
      </div>
      {conflicts && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={(e) => { e.stopPropagation(); setConflicts(null); }}>
          <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="bundle-conflicts">
            <h3 className="font-bold">Der findes allerede regler for præcis de samme ture</h3>
            <ul className="text-sm list-disc pl-5">{conflicts.map(x => <li key={x.id}>{x.name}{x.category ? ` (${options.categories[x.category] || x.category})` : " (grundpris)"}</li>)}</ul>
            <p className="text-sm text-slate-600 dark:text-slate-300">Hvis du gemmer alligevel, vinder den nyeste. Overvej i stedet at rette den eksisterende aftale.</p>
            <div className="flex justify-end gap-2">
              <button className={btnGhost} onClick={() => setConflicts(null)}>Tilbage</button>
              <button className={btnPrimary} onClick={() => { setConflicts(null); save(true); }}>Gem alligevel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ======================================================================
   Godkendelser
   ====================================================================== */
function ApprovalsView({ onChanged }) {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try { setItems((await axios.get(`${API}/pricing/requests?status=${status}`)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, approve) => {
    try {
      await axios.post(`${API}/pricing/requests/${id}/decide`, { approve, note });
      toast.success(approve ? "Rettelsen er godkendt" : "Rettelsen er afvist");
      setRejecting(null); setNote(""); load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const diff = (r) => {
    const b = r.before || {}, c = r.changes || {};
    const out = [];
    if ("start_time" in c) out.push(["Start", b.start_time || "-", c.start_time]);
    if ("end_time" in c) out.push(["Slut", b.end_time || "-", c.end_time]);
    if ("used_trailer" in c) out.push(["Hænger", b.used_trailer ? "Med" : "Uden", c.used_trailer ? "Med" : "Uden"]);
    const tn = (n) => (Number(n) === 0 ? "Ingen" : `${n} hænger${Number(n) === 1 ? "" : "e"}`);
    if ("trailer_count" in c) out.push(["Hængere", tn(b.trailer_count ?? (b.used_trailer ? 1 : 0)), tn(c.trailer_count)]);
    if ("waiting_min" in c) out.push(["Ventetid", `${b.waiting_min || 0} min`, `${c.waiting_min} min`]);
    return out;
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {[["pending", "Afventer"], ["approved", "Godkendt"], ["rejected", "Afvist"]].map(([v, l]) => (
          <button key={v} className={`${chipBase} ${status === v ? chipOn : chipOff}`} onClick={() => setStatus(v)}>{l}</button>
        ))}
      </div>
      {!items ? <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        : items.length === 0 ? <div className={`${card} p-10 text-center text-sm text-slate-500`}>{status === "pending" ? "Ingen rettelser venter. Chauffører kan anmode om rettelser på afsluttede ture." : "Ingen her endnu."}</div>
          : items.map(r => (
            <div key={r.id} className={`${card} p-4 space-y-3`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-bold">{r.driver_name} <span className="font-normal text-slate-500">· {fmtDate(r.service_date)} · {r.tour_number}</span></div>
                  <div className="text-sm text-slate-500">{r.tour_label}</div>
                </div>
                <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString("da-DK")}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {diff(r).map(([k, from, to]) => (
                  <div key={k} className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-900 text-sm">
                    <div className="text-[11px] font-bold uppercase text-slate-500">{k}</div>
                    <span className="line-through text-slate-400">{from}</span> → <b>{to}</b>
                  </div>
                ))}
              </div>
              <div className="text-sm italic text-slate-600 dark:text-slate-300">"{r.comment}"</div>
              {r.status === "pending" && (r.warnings || []).map(w => (
                <div key={w} className="flex gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs text-amber-900 dark:text-amber-100" data-testid="request-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> {w}
                </div>
              ))}
              {r.status === "pending" ? (
                rejecting === r.id ? (
                  <div className="flex flex-wrap gap-2">
                    <input className={`${input} flex-1 min-w-[200px]`} placeholder="Hvorfor afvises den?" value={note} onChange={e => setNote(e.target.value)} />
                    <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold" onClick={() => decide(r.id, false)}><Ban className="w-4 h-4" /> Afvis</button>
                    <button className={btnGhost} onClick={() => { setRejecting(null); setNote(""); }}>Annuller</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button className={btnPrimary} onClick={() => decide(r.id, true)}><Check className="w-4 h-4" /> Godkend</button>
                    <button className={btnGhost} onClick={() => setRejecting(r.id)}><Ban className="w-4 h-4" /> Afvis</button>
                  </div>
                )
              ) : (
                <div className="text-xs text-slate-500">{r.status === "approved" ? (r.auto_approved ? "Godkendt automatisk" : "Godkendt") : "Afvist"}{r.decided_by ? ` af ${r.decided_by}` : ""} · {new Date(r.decided_at).toLocaleString("da-DK")}{r.decision_note ? ` · "${r.decision_note}"` : ""}</div>
              )}
            </div>
          ))}
    </div>
  );
}

/* ======================================================================
   Helligdage
   ====================================================================== */
function HolidaysView({ profile }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [newDay, setNewDay] = useState({ date: "", name: "" });

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/pricing/holidays?year=${year}`)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try { await axios.put(`${API}/pricing/settings`, patch); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!data) return <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const s = data.settings;
  const keyByName = Object.fromEntries(Object.entries(data.optional_keys).map(([k, v]) => [v, k]));

  const toggleDay = (d) => {
    if (d.kind === "optional") {
      const key = keyByName[d.name];
      const cur = new Set(s.holiday_optional || []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      save({ holiday_optional: Array.from(cur) });
    } else if (d.kind === "official") {
      const cur = new Set(s.holiday_excluded || []);
      cur.has(d.date) ? cur.delete(d.date) : cur.add(d.date);
      save({ holiday_excluded: Array.from(cur) });
    }
  };
  const addExtra = () => {
    if (!newDay.date) { toast.error("Vælg en dato"); return; }
    save({ holiday_extra: [...(s.holiday_extra || []), { date: newDay.date, name: newDay.name || "Ekstra fridag" }] });
    setNewDay({ date: "", name: "" });
  };
  const removeExtra = (date) => save({ holiday_extra: (s.holiday_extra || []).filter(x => x.date !== date) });
  const KIND = { official: ["Officiel", "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"], optional: ["Valgfri", "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"], extra: ["Egen dag", "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"] };

  return (
    <div className="space-y-4">
      {!profile?.has_connections ? (
        <div className={`${card} p-4 text-sm text-slate-600 dark:text-slate-300`}>
          <b>Din helligdagskalender herunder bruges til alle ture.</b> Bliver I tilknyttet en genbrugsplads-kunde,
          kan I her vælge at bruge kundens egen kalender for netop den kundes ture.
        </div>
      ) : (
      <div className={`${card} p-4 space-y-3`}>
        <h3 className="font-bold">Hvilken kalender bestemmer om en dag er helligdag?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[["customer_first", "Kundens kalender først", "Lokationens helligdagskalender (den samme som bestillingssiden bruger) → kundens helligdagsgruppe → din egen kalender herunder, hvis kunden ikke har nogen."],
            ["own", "Altid min egen kalender", "Kun kalenderen herunder bruges, uanset hvad kunderne har sat op."]].map(([v, t, desc]) => (
            <button key={v} onClick={() => save({ holiday_source: v })} className={`text-left p-3 rounded-xl border-2 ${s.holiday_source === v ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-slate-200 dark:border-slate-700"}`}>
              <div className="font-semibold text-sm">{t}</div><div className="text-xs text-slate-500 mt-1">{desc}</div>
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500">{profile?.tier === "partner" ? "Hver kundes kalender gælder for den kundes ture." : "Kundens kalender gælder kun for den kundes ture - dine egne ture bruger altid din egen kalender."} Om et tillæg gælder på helligdage styres i prisreglerne (dagtype "Helligdag"), så et helligdagstillæg kan gælde for ét materiale eller én kunde og ikke for andre.</p>
      </div>
      )}

      <div className={`${card} overflow-hidden`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-700">
          <h3 className="font-bold">Min helligdagskalender</h3>
          <div className="flex items-center gap-2">
            <button className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => setYear(y => y - 1)}><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-bold w-12 text-center">{year}</span>
            <button className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" onClick={() => setYear(y => y + 1)}><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 dark:bg-slate-900/40">Danske helligdage beregnes automatisk for hvert år (også påske og pinse). Slå dage til/fra, eller tilføj jeres egne.</div>
        {data.days.map(d => (
          <div key={d.date} className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-mono text-sm w-32 shrink-0 whitespace-nowrap">{fmtDate(d.date)}</span>
              <span className={`text-sm ${d.active ? "" : "text-slate-400 line-through"}`}>{d.name}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${KIND[d.kind][1]}`}>{KIND[d.kind][0]}</span>
            </div>
            {d.kind === "extra" ? (
              <button className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30" onClick={() => removeExtra(d.date)}><Trash2 className="w-4 h-4" /></button>
            ) : (
              <button onClick={() => toggleDay(d)} className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${d.active ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${d.active ? "left-[18px]" : "left-0.5"}`} />
              </button>
            )}
          </div>
        ))}
        <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
          <input type="date" className={`${inputFit} w-44`} value={newDay.date} onChange={e => setNewDay({ ...newDay, date: e.target.value })} />
          <input className={`${input} flex-1 min-w-[160px]`} placeholder="Navn, fx Firmafest" value={newDay.name} onChange={e => setNewDay({ ...newDay, name: e.target.value })} />
          <button className={btnPrimary} onClick={addExtra}><Plus className="w-4 h-4" /> Tilføj dag</button>
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
   Indstillinger
   ====================================================================== */
/** Afvigelseskoder chaufføren vælger mellem (forgæves tur, stop der ikke kunne gennemføres, afvigelse ved afslutning). */
function DeviationCodes({ codes, onSave }) {
  const [list, setList] = useState(codes);
  const [nw, setNw] = useState("");
  useEffect(() => setList(codes), [codes]);
  // 2026-10-25: En kode, der er skrevet uden at trykke Tilføj, kommer med ved Gem.
  const pending = nw.trim() && !list.includes(nw.trim()) ? nw.trim() : "";
  const changed = JSON.stringify(list) !== JSON.stringify(codes) || !!pending;
  return (
    <div className={`${card} p-4 space-y-3`} data-testid="settings-codes">
      <h3 className="font-bold">Afvigelseskoder</h3>
      <p className="text-sm text-slate-500">Det chaufføren kan vælge, når en tur eller et stop ikke kan gennemføres, eller når noget afviger. Koderne gør det muligt at se hvor ofte det sker - og at fakturere forgæves kørsel.</p>
      <div className="flex flex-wrap gap-1.5">
        {list.map(c => (
          <span key={c} className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-sm">{c}
            <button type="button" className="p-0.5 text-slate-400 hover:text-red-600" aria-label={`Fjern ${c}`} onClick={() => setList(list.filter(x => x !== c))}><X className="w-3.5 h-3.5" /></button></span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <input className={`${input} max-w-xs`} value={nw} onChange={e => setNw(e.target.value)} placeholder="Ny kode, fx Container blokeret" maxLength={40}
          onKeyDown={e => { if (e.key === "Enter" && nw.trim()) { setList([...list, nw.trim()]); setNw(""); } }} />
        <button type="button" className={btnGhost} onClick={() => { if (nw.trim()) { setList([...list, nw.trim()]); setNw(""); } }}><Plus className="w-4 h-4" /> Tilføj</button>
        {changed && <button type="button" className={btnPrimary} onClick={() => { onSave(pending ? [...list, pending] : list); setNw(""); }} data-testid="settings-codes-save"><Check className="w-4 h-4" /> Gem koder</button>}
      </div>
    </div>
  );
}

/** 2026-10-18: Afvigelseskoder og "ét afslutningsark" er drift - chaufføren
 *  bruger dem uanset priser. De ligger under logistik admin › Indstillinger
 *  (rettigheden "Indstillinger") og gemmes via /company/finish-settings. */
export function FinishSettingsCard() {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/company/finish-settings`).then(r => setS(r.data)).catch(e => toast.error(formatApiError(e))); }, []);
  const save = async (patch) => {
    try { setS((await axios.put(`${API}/company/finish-settings`, patch)).data); toast.success("Gemt"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!s) return <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="finish-settings">
      <DeviationCodes codes={s.deviation_codes || []} onSave={(codes) => save({ deviation_codes: codes })} />
      <div className={`${card} p-4 space-y-3`} data-testid="settings-unified">
        <h3 className="font-bold">Chaufførens afslutning</h3>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="accent-emerald-600 w-4 h-4 mt-0.5" checked={!!s.unified_finish} onChange={e => save({ unified_finish: e.target.checked })} />
          <span><b>Ét afslutningsark for alle ture</b> - også bestillinger fra genbrugspladser (B2B). Vægten er stadig påkrævet på containerafhentninger, løs afhentning vejes pr. læs, og vejesedlen kan vedhæftes.
            <span className="block text-xs text-slate-500 mt-0.5">Slået fra: B2B-ture bruger det hidtidige vægt-flow. Slå det til for én dag og se om chaufførerne er glade for det.</span></span>
        </label>
      </div>
      <div className={`${card} p-4 space-y-3`} data-testid="settings-driver-create">
        <h3 className="font-bold">Chauffører opretter ture</h3>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="accent-emerald-600 w-4 h-4 mt-0.5" checked={!!s.driver_create_tours} onChange={e => save({ driver_create_tours: e.target.checked })} data-testid="settings-driver-create-toggle" />
          <span><b>Chauffører må oprette en tur i appen</b> - fx når en kunde bestiller i sit eget system (som Pickup), og chaufføren skal lægge turen ind.
            <span className="block text-xs text-slate-500 mt-0.5">Chaufføren vælger plads, materiale, container, tøm/byt og skriver kundens ordrenummer. På en genbrugsplads-kundes plads bliver det kundens bestilling (ligesom "Bestil for kunden") - kunden kan se den, og den prissættes og faktureres som andre ture. Samme ordrenummer kan ikke oprettes to gange.</span></span>
        </label>
      </div>
    </div>
  );
}

// 2026-10-25: Hængerkørsel med en container fra en anden plads.
const CROSS_MODES = [
  ["separate", "Fuld pris for hver (anbefalet)", "Hver container prissættes som kørt alene - grundpris, ingen hængerpris og fuld tid ved timepris. Containere fra samme plads får stadig hængerprisen."],
  ["run", "Hængerpris som ved samme plads", "Pladsen er ligegyldig: containeren på hængeren får hængerprisen fra aftalen."],
];
const crossLabel = (m) => (m === "run" ? "hængerpris som ved samme plads" : "fuld pris for hver");
const todayIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Copenhagen" });
const fmtIsoDk = (d) => (d ? `${Number(d.slice(8, 10))}.${Number(d.slice(5, 7))}.${d.slice(0, 4)}` : "");

/** 2026-10-25: Undtagelser pr. kunde - en kunde med flere pladser kan have
 *  aftalt hængerpris på tværs af sine egne pladser (eller omvendt). Hver
 *  linje gælder fra en kørselsdato; en ny aftale = en ny linje. */
function CrossExceptions({ rows, onSave }) {
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState({ customer_key: "", mode: "run", from: todayIso() });
  useEffect(() => { axios.get(`${API}/pricing/options`).then(r => setOpts(r.data)).catch(() => setOpts({ customers: [] })); }, []);
  const customers = opts?.customers || [];
  const add = () => {
    const c = customers.find(x => x.id === f.customer_key);
    if (!c) return;
    onSave([...rows, { customer_key: c.id, customer_name: c.name, mode: f.mode, from: f.from || todayIso() }]);
    setF({ customer_key: "", mode: "run", from: todayIso() });
  };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="settings-run-cross-customers">
      <div className="text-sm font-semibold">Undtagelser pr. kunde</div>
      <p className="text-xs text-slate-500">Har en kunde en anden aftale end standarden - fx hængerpris på tværs af kundens egne pladser - så tilføj den her. Gælder fra kørselsdatoen; ændres aftalen igen, så tilføj en ny linje med den nye dato.</p>
      {rows.length > 0 && (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700 text-sm">
          {rows.map((r, i) => (
            <li key={`${r.customer_key}-${r.from}`} className="py-1.5 flex items-center gap-2">
              <span className="flex-1 min-w-0 truncate"><b>{r.customer_name || r.customer_key}</b> · {crossLabel(r.mode)} · fra {fmtIsoDk(r.from)}</span>
              <button type="button" className="p-1 text-slate-400 hover:text-red-600" aria-label="Fjern undtagelsen"
                title="Fjern - ændrer prisen på ikke-fakturerede ture kørt efter datoen"
                onClick={() => onSave(rows.filter((_, j) => j !== i))}><X className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select className={`${inputFit} min-w-[12rem]`} value={f.customer_key} onChange={e => setF({ ...f, customer_key: e.target.value })} data-testid="settings-run-cross-customer">
          <option value="">Vælg kunde…</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className={inputFit} value={f.mode} onChange={e => setF({ ...f, mode: e.target.value })} data-testid="settings-run-cross-customer-mode">
          <option value="run">Hængerpris som ved samme plads</option>
          <option value="separate">Fuld pris for hver</option>
        </select>
        <span className="text-xs text-slate-500">fra</span>
        <input type="date" className={`${inputFit} w-40`} value={f.from} onChange={e => setF({ ...f, from: e.target.value })} />
        <button type="button" className={btnGhost} disabled={!f.customer_key} onClick={add} data-testid="settings-run-cross-customer-add"><Plus className="w-4 h-4" /> Tilføj</button>
      </div>
    </div>
  );
}

function SettingsView() {
  const [s, setS] = useState(null);
  const [crossFrom, setCrossFrom] = useState(todayIso());
  useEffect(() => { axios.get(`${API}/pricing/settings`).then(r => setS(r.data)).catch(e => toast.error(formatApiError(e))); }, []);
  const save = async (patch) => {
    try { setS((await axios.put(`${API}/pricing/settings`, patch)).data); toast.success("Gemt"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!s) return <div className={`${card} p-10 flex justify-center`}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4">
      <div className={`${card} p-4 space-y-3`}>
        <h3 className="font-bold flex items-center gap-2"><Truck className="w-4 h-4" /> Samtidige ture (timepris)</h3>
        <p className="text-sm text-slate-500">Med hænger kan en chauffør have flere ture i gang på samme tid. Hvordan skal den fælles tid afregnes?</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[["split", "Fordel tiden ligeligt", "1 time med 2 ture i gang = 30 min på hver tur. Ingen kunde betaler for den samme time to gange."],
            ["full", "Fuld tid på hver tur", "1 time med 2 ture i gang = 1 time på hver tur."]].map(([v, t, desc]) => (
            <button key={v} onClick={() => save({ concurrent_mode: v })} className={`text-left p-3 rounded-xl border-2 ${s.concurrent_mode === v ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-slate-200 dark:border-slate-700"}`}>
              <div className="font-semibold text-sm">{t}</div><div className="text-xs text-slate-500 mt-1">{desc}</div>
            </button>
          ))}
        </div>
        {/* 2026-10-24 (B13): Timeprisen og lønnen regner pause forskelligt - med vilje. */}
        <p className="text-xs text-slate-500" data-testid="settings-pause-note">Pause i timeprisen: kun pause, chaufføren har stemplet, trækkes fra kundens tid. Den faste pause i lønnen (Løn › Regler) trækkes ikke her - kunden betaler for den tid, chaufføren er på opgaven.</p>
      </div>
      <div className={`${card} p-4 space-y-3`} data-testid="settings-run-cross">
        <h3 className="font-bold flex items-center gap-2"><Truck className="w-4 h-4" /> Hængerkørsel med containere fra forskellige pladser</h3>
        <p className="text-sm text-slate-500">Henter chaufføren en container på en anden plads end bilens (fx to containere, der skal samme sted hen), kører de sammen - men er det stadig to opgaver?</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {CROSS_MODES.map(([v, t, desc]) => (
            <button key={v} onClick={() => (s.run_cross_location || "separate") !== v && save({ run_cross_location: v, run_cross_location_from: crossFrom })} data-testid={`settings-run-cross-${v}`}
              className={`text-left p-3 rounded-xl border-2 ${(s.run_cross_location || "separate") === v ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-slate-200 dark:border-slate-700"}`}>
              <div className="font-semibold text-sm">{t}</div><div className="text-xs text-slate-500 mt-1">{desc}</div>
            </button>
          ))}
        </div>
        <label className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          Et skift gælder ture kørt fra
          <input type="date" className={`${inputFit} w-40`} value={crossFrom} onChange={e => setCrossFrom(e.target.value || todayIso())} data-testid="settings-run-cross-from" />
          - tidligere ture beholder den regel, der gjaldt, da de blev kørt. Fakturerede ture ændres aldrig.
        </label>
        {(s.run_cross_location_log || []).length > 1 && (
          <p className="text-xs text-slate-500" data-testid="settings-run-cross-log">
            Historik: {(s.run_cross_location_log || []).map(e => `${e.from <= "2000-01-01" ? "før" : `fra ${fmtIsoDk(e.from)}`}: ${crossLabel(e.mode)}`).join(" · ")}
          </p>
        )}
        <CrossExceptions rows={s.run_cross_location_customers || []} onSave={(rows) => save({ run_cross_location_customers: rows })} />
        <p className="text-xs text-slate-500">En enkelt tur kan altid rettes under Rapport › Tilpas › Kørsel.</p>
      </div>
      <div className={`${card} p-4 space-y-3`} data-testid="settings-waiting">
        <h3 className="font-bold">Ventetid fra chaufføren</h3>
        <p className="text-sm text-slate-500">Chaufføren oplyser ventetid ved afslutning. Kort ventetid kan godkendes automatisk, så du kun skal se på de længere.</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>Godkend automatisk op til</span>
          <input type="number" min="0" max="120" className={`${inputFit} w-24`} defaultValue={s.auto_approve_waiting_min || 0}
            onBlur={e => { const v = parseDk(e.target.value); if (!(v >= 0 && v <= 120)) { toast.error("Skriv 0-120 minutter"); e.target.value = s.auto_approve_waiting_min || 0; return; }
              if (v !== (s.auto_approve_waiting_min || 0)) save({ auto_approve_waiting_min: v }); }} data-testid="settings-auto-waiting" />
          <span>min</span>
          <span className="text-xs text-slate-500">(0 = du godkender altid selv)</span>
        </div>
      </div>
      <div className={`${card} p-4 space-y-2`} data-testid="settings-driver-price">
        <h3 className="font-bold">Chaufføren og priserne</h3>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.driver_sees_price} onChange={e => save({ driver_sees_price: e.target.checked })} /> Chaufføren ser kundens timepris (kr/t) på turen</label>
        <p className="text-xs text-slate-500">Uden flueben ser chaufføren kun, at tiden afregnes (og om pause trækkes fra) - ikke beløbet.</p>
      </div>
      <p className="text-xs text-slate-500 px-1" data-testid="settings-moved-note">Afvigelseskoder og chaufførens afslutning ligger under Indstillinger (i menuen "Mere").</p>
      <div className={`${card} p-4 space-y-2`}>
        <h3 className="font-bold">Moms</h3>
        <p className="text-sm text-slate-500">Priser i reglerne er ekskl. moms. Rapporten viser moms separat.</p>
        <div className="flex items-center gap-2">
          {/* 2026-10-25: Et tomt eller ugyldigt felt gemte før 0 % moms. */}
          <input inputMode="decimal" className={`${inputFit} w-28`} defaultValue={String(s.vat_percent ?? "").replace(".", ",")} data-testid="settings-vat"
            onBlur={e => { const v = parseDk(e.target.value); if (!(v >= 0 && v <= 100)) { toast.error("Skriv momsen i procent, fx 25"); e.target.value = String(s.vat_percent ?? "").replace(".", ","); return; }
              if (v !== s.vat_percent) save({ vat_percent: v }); }} />
          <span className="text-sm">%</span>
        </div>
      </div>
    </div>
  );
}


/* 2026-10-25: Prisregler rettet med virkning bagud (fx en prisaftale fra 1.10, rettet
   25.10): de fakturerede ture står med den gamle pris. Her ses de - og der laves kun
   korrektioner, når nogen beslutter det (en faktura ændres aldrig af sig selv). */
function RetroCard({ items, onDone }) {
  const [sel, setSel] = useState(() => new Set(items.map((x) => x.tour_id)));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const chosen = items.filter((x) => sel.has(x.tour_id));
  const sum = chosen.reduce((a, x) => a + x.delta, 0);
  const create = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/pricing/retro-corrections`, { tour_ids: chosen.map((x) => x.tour_id), note });
      toast.success(`${r.created} korrektioner (${fmtKr(r.total)}) - de kommer på kundernes næste faktura`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className={`${card} p-4 space-y-2 border-amber-200`} data-testid="report-retro">
      <div className="font-bold text-sm">Fakturerede ture, hvor prisen i dag ville være en anden ({items.length})</div>
      <p className="text-xs text-slate-500">Prisreglerne (eller helligdagene) er ændret med virkning bagud, efter turene blev faktureret. Fakturaerne ændres ikke af sig selv: vælg de ture, hvor kunden skal have forskellen, så kommer den som en korrektion på næste faktura. Var den gamle pris aftalt, så lad være.</p>
      <ul className="divide-y divide-slate-100 dark:divide-slate-700 text-sm">
        {items.map((x) => (
          <li key={x.tour_id} className="py-1.5 flex items-center gap-2">
            <input type="checkbox" className="accent-emerald-600" checked={sel.has(x.tour_id)}
              onChange={(e) => { const n = new Set(sel); e.target.checked ? n.add(x.tour_id) : n.delete(x.tour_id); setSel(n); }} />
            <span className="flex-1 min-w-0"><b>{x.customer_name}</b> · {x.tour_number || "tur"} {fmtDate(x.service_date)}{x.invoice_no ? ` · faktura ${x.invoice_no}` : ""} · faktureret {fmtKr(x.invoiced)} → i dag {fmtKr(x.now)}</span>
            <span className={`font-bold ${x.delta < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtKr(x.delta)}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${input} flex-1 min-w-[220px]`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Begrundelse, fx prisaftalen rettet fra 1.10" data-testid="retro-note" />
        <button className={btnPrimary} disabled={busy || !chosen.length || !note.trim()} onClick={create} data-testid="retro-create">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Opret {chosen.length} korrektioner ({fmtKr(sum)})</button>
      </div>
    </div>
  );
}
